import React from 'react';
import * as Label from "@radix-ui/react-label";
import { motion } from "framer-motion";
import { useTheme } from "../Theme/ThemeProvider";
import { 
  PaperPlaneIcon, 
  FileIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon
} from "@radix-ui/react-icons";
import { ReactComponent as ReasoningIconSVG } from '../Icon/ReasoningIcon.svg';
import { ReactComponent as DataAnalysisIconSVG } from '../Icon/DataAnalysisIcon.svg';
import { ReactComponent as FileUploadIconSVG } from '../Icon/FileUploadIcon.svg';
import { MdTranslate } from "react-icons/md";
import { TbWorldSearch } from 'react-icons/tb';
import { PiImage } from 'react-icons/pi';
import { AudioLines, Mic, MicOff, X, Check } from 'lucide-react';
import { useWhisperDictation } from '../../hooks/useWhisperDictation';
import { dictationService } from '../../utils/dictation.service';
import { DictationWaveform } from './DictationWaveform';
import { IconType } from 'react-icons';
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PreviewableFileAttachment } from './PreviewableFileAttachment';
import { AudioWaveform } from '../VoiceMode/AudioWaveform';
import { VoiceCircle } from '../VoiceMode/VoiceCircle';
import { BsFillStopFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { addUploadedFile, removeUploadedFile, clearUploadedFiles, updateUploadedFile, ensureSessionId, clearTargetLanguages, setLastTranslation, setIsTranslating, setTranslationQuota, clearTranslationData, setTranslationHistory, addTranslationMessage, setTranslationError } from '../../store/chatSlice';
import { PLUS_MENU_STEP_IDS } from '../../store/tutorialSlice';
import type { ChatModeState } from '../../store/chatModeSlice';
import { setSelectedTool, setSelectedModel } from '../../store/chatModeSlice';
import { FileUploadDialog, FileSelectionWarningDialog } from '../Dialogs/FileUploadDialog';
import { hideGlobalBlocking, showGlobalBlocking } from '../../store/loadingSlice';
import { useMsal } from '@azure/msal-react';
import { convertFileToPdf } from '../../utils/fileConversion.service';
import { createDocumentTranslationService } from '../../utils/documentTranslation.service';
import { buildTranslationMessageContent } from '../../utils/translationMessageContent';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { MicrophonePermissionDialog } from '../Dialogs/MicrophonePermissionDialog';
import { MicrophoneSelectorDialog } from '../Dialogs/MicrophoneSelectorDialog';
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import logger from "../../utils/logger";
import { utf8ToBase64, arrayBufferToBase64 } from '../../utils/base64.utils';
import { ILanguageOption } from './LanguageSelector';
import { TranslationMode } from './TranslationMode';
import { InactivityWarningDialog } from '../Dialogs/InactivityWarningDialog';
import { useVoiceModeHandlers } from '../../hooks/useVoiceModeHandlers';
import { authSelector } from "../../features/auth/authSlice";
import { MODEL_ELIGIBLE_TOOLS, isModelSelectorVisible, getToolForModelSelection, getVisibleModelOptions, MODEL_OPTIONS, ModelConfig } from '../../utils/modelConfig';
import { getRagPlaceholder } from '../../utils/ragConfig';
import { useFileInputCancelListener } from '../../hooks/useFileInputCancelListener';

const MAX_MB = 5;

// Wrapper for ReasoningIcon SVG — maps `size` to width/height for consistency with react-icons
const ReasoningModelIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <ReasoningIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

const DataAnalysisIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <DataAnalysisIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

const FileUploadIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <FileUploadIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

// Wrapper to ensure a valid JSX.Element type using React.createElement
const StopIcon: React.FC<{ size?: number; color?: string }> = ({ size = 28, color = '#D10411' }) => {
  const icon = React.createElement(BsFillStopFill as any, { size, color });
  return React.createElement('span', { style: { display: 'inline-flex', lineHeight: 0 } }, icon) as React.ReactElement;
};

interface ChatOption {
  label: string;
  icon: React.ComponentType<any> | IconType;
  apiLabel: string;
}

interface ChatInputProps {
  onHeightChange?: (height: number) => void;
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  onAddMessage?: (message: any) => void;
  isStreaming?: boolean;
  onStopStream?: () => void;
  chatPage?: boolean;
  initialTools?: string[];
  isImageStreaming?: boolean; // specifically track image generation streaming state
  error?: Error | null; // propagate error state to adjust generating UI
  externalValue?: string; // external value to set in the input
  onExternalValueApplied?: () => void; // callback after external value is applied
  onClearChat?: () => void; // callback to clear chat history when switching tools
  hasMessages?: boolean; // whether chat has any messages
  lastConversationTool?: string | null;
  iconSize?: number;
  isWideScreen?: boolean; // from ChatPage, true if above threshold
}

/** Aborts every active FileReader in the ref array and empties the array. */
function abortAndClearReaders(readersRef: React.RefObject<FileReader[]>) {
  readersRef.current.forEach(r => r.abort());
  readersRef.current = [];
}

export function ChatInput({ onHeightChange, onNavigateToChat, onAddMessage, isStreaming, onStopStream, chatPage, initialTools, isImageStreaming, error, externalValue, onExternalValueApplied, onClearChat, hasMessages, lastConversationTool, iconSize, isWideScreen }: ChatInputProps) {
  // Place chatModeState selector at the very top so it's available everywhere
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);
  const selectedTool = chatModeState.selectedTool;
  // Track if Summarize mode is active (visual indicator and auto-filled prompt)
  const [isSummarizeActive, setIsSummarizeActive] = React.useState(false);
  const uploadedFiles = useSelector((state: RootState) => state.chat.uploadedFiles);
  const [isFileUploadActive, setIsFileUploadActive] = React.useState(false);

  const prevModeRef = React.useRef(chatModeState.mode);
  const prevRagChatTypeRef = React.useRef(chatModeState.ragChatType);
  const prevSelectedModelIdRef = React.useRef(chatModeState.selectedModelId);
  const dispatch = useDispatch();

  // Clear uploaded files whenever the user switches to an image model
  React.useEffect(() => {
    const prevId = prevSelectedModelIdRef.current;
    prevSelectedModelIdRef.current = chatModeState.selectedModelId;
    if (prevId === chatModeState.selectedModelId) return;
    if (getToolForModelSelection(chatModeState.selectedModelId) === 'image_generation') {
      if (uploadedFilesRef.current.length > 0) {
        dispatch(clearUploadedFiles());
        uploadedFilesRef.current = [];
      }
      setIsFileUploadActive(false);
    }
  }, [chatModeState.selectedModelId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selectedTool whenever the chat mode or RAG type actually changes (skip initial mount)
  React.useEffect(() => {
    const modeChanged = prevModeRef.current !== chatModeState.mode;
    const ragChanged = prevRagChatTypeRef.current !== chatModeState.ragChatType;
    prevModeRef.current = chatModeState.mode;
    prevRagChatTypeRef.current = chatModeState.ragChatType;

    if ((modeChanged || ragChanged) && selectedTool !== null) {
      dispatch(setSelectedTool(null));
      setIsTranslationCollapsed(false);
    }
  }, [chatModeState.mode, chatModeState.ragChatType]); // eslint-disable-line react-hooks/exhaustive-deps
  const auth = useSelector(authSelector);
  const [value, setValue] = React.useState("");
  const previousToolRef = React.useRef<string | null>(null); // Track previous tool for clearing chat on switch
  const manuallyClearedToolRef = React.useRef(false); // track manual clears to avoid auto re-select
  const [toolTextWidth, setToolTextWidth] = useState(80);
  const { accent, mode } = useTheme();
  // Track first tool used to determine which tools to show/hide
  const [firstToolUsed, setFirstToolUsed] = useState<string | null>(null);
  // Dropdown removed; tools now always visible as bubbles.
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const mobileImageInputRef = React.useRef<HTMLInputElement>(null);
  const bottomBubbleContainerRef = React.useRef<HTMLDivElement>(null);

  // Ref mirror of uploadedFiles — lets proceedWithToolSelection read the latest value without
  // being in its useCallback dependency array (avoids re-registering the gallery event listener
  // on every file add/remove). Setting a ref during render is safe: the render always completes
  // before any callback can fire.
  const uploadedFilesRef = React.useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;
  // (conversationStarted no longer gates thumbnails; removed selector to avoid unused lint warning)
  const sessionId = useSelector((state: RootState) => state.chat.sessionId);
  const globalBlocking = useSelector((state: RootState) => state.loading.globalBlocking);
  const { instance: msalInstance } = useMsal();

  // Speech-to-text via Azure Whisper (server-side, works on ALL browsers)
  const {
    isListening: isSpeechListening,
    transcript: speechTranscript,
    isTranscribing: isSpeechTranscribing,
    isStarting: isSpeechStarting,
    error: speechError,
    audioLevel: speechAudioLevel,
    frequencyData: speechFrequencyData,
    startListening: startSpeech,
    stopListening: stopSpeech,
    confirmDictation,
    cancelDictation,
    resetTranscript: resetSpeech,
    mode: dictationMode,
  } = useWhisperDictation();
  // Snapshot of textarea value captured at the moment dictation starts.
  // Live-preview is rendered as: preDictationValueRef + speechTranscript
  // directly inside the textarea value prop — not via placeholder — so it is
  // always visible regardless of whether the textarea already has content.
  const preDictationValueRef = React.useRef<string>('');
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive, isListening: isVoiceModeListening, isPaused: isVoiceModePaused, isRestarting: isVoiceModeRestarting, showInactivityWarning, inactivityCountdown, disconnectedByInactivity, showNoAudioDialog } = voiceModeState;

  // Voice mode handlers (loading state, dialogs, start/stop/pause, mic selector, etc.)
  const {
    voiceModeLoading,
    audioLevel,
    frequencyData,
    showMicPermissionDialog,
    setShowMicPermissionDialog,
    showVoiceModeError,
    setShowVoiceModeError,
    voiceModeErrorMessage,
    showMicSelector,
    setShowMicSelector,
    handleVoiceModeButtonClick,
    handleDismissInactivityWarning,
    handleVoiceModePauseToggle,
    handleVoiceModeStop,
    handleMicrophoneSelected,
    handleNoAudioDialogConfirm,
    handleNoAudioDialogDismiss,
  } = useVoiceModeHandlers({ onNavigateToChat, chatPage });

  const normalizeConversationTool = React.useCallback((tool: string | null | undefined) => {
    return tool || 'regular_chat';
  }, []);

  const shouldClearForTargetTool = React.useCallback((targetTool: string | null | undefined) => {
    if (!hasMessages) {
      return false;
    }

    return normalizeConversationTool(lastConversationTool) !== normalizeConversationTool(targetTool);
  }, [hasMessages, lastConversationTool, normalizeConversationTool]);

  // New explicit state: true only while we have opened the native file picker and are awaiting user selection/cancel.
  // This replaces previous heuristic of showing spinner whenever tool selected & no files.
  const [awaitingFileSelection, setAwaitingFileSelection] = useState(false);

  // Spinner for Data Analysis file upload — true when actively uploading OR awaiting confirmation
  const isDataAnalysisUploading = selectedTool === 'data_analysis' &&
    (uploadedFiles.some(f => f.status === 'uploading' || f.status === 'pending') || awaitingFileSelection);

  // Ref to track when files are being processed (since Redux uploadedFiles updates async)
  const filesBeingProcessedRef = React.useRef(false);
  // Ref to track awaitingFileSelection for focus handler (avoids stale closure)
  const awaitingFileSelectionRef = React.useRef(false);
  // Ref to track active FileReader instances for translation mode (so they can be aborted on cancel)
  const translationActiveReadersRef = React.useRef<FileReader[]>([]);
  // Ref to track active FileReader instances when FileUploadDialog is open (so they can be aborted on cancel)
  const dialogActiveReadersRef = React.useRef<FileReader[]>([]);
  // Ref to track active FileReader instances for direct general-chat drag/drop uploads.
  const generalActiveReadersRef = React.useRef<FileReader[]>([]);
  // Tracks only files added by the active no-tool direct-drop batch so cancel can roll them back.
  const directDropBatchFileNamesRef = React.useRef<string[]>([]);
  const isDirectDropBatchActiveRef = React.useRef(false);
  // Dictation error dialog state
  const [showDictationError, setShowDictationError] = React.useState(false);

  // Unified file warning state — covers both general and translation mode warnings
  const [fileWarning, setFileWarning] = React.useState<{
    title?: string | null;
    subtitle?: string | null;
    message: string;
    mode: 'general' | 'translation';
  } | null>(null);

  // Drag and drop state
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [showBlockingMessage, setShowBlockingMessage] = React.useState(false);
  const [showImmediateSpinner, setShowImmediateSpinner] = React.useState(false); // Local spinner for instant feedback
  const [isFileDragInProgress, setIsFileDragInProgress] = React.useState(false); // Track if user is dragging file anywhere on window
  
  // Refs to track drag state for event handlers (avoids stale closures)
  const isFileDragInProgressRef = React.useRef(false);
  
  // Check if tools should be hidden
  // - Always hide for 3M Knowledge mode when any RAG is selected
  // - Never hide when mode is 'Ask Chat', regardless of the remembered ragChatType
  const shouldHideTools = chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType !== null;
  const isDraggingOverRef = React.useRef(false);

  // Dynamic placeholder based on RAG type
  const getPlaceholder = () => {
    // Show standard placeholder for any selected tool (never for compare)
    if (selectedTool) {
      if (selectedTool === 'reasoning') {
        const selectedModel = MODEL_OPTIONS.find((m: ModelConfig) => m.id === chatModeState.selectedModelId);
        const modelName = selectedModel?.displayName.split(' - ')[0] || "Reasoning";
        return `Using ${modelName} Reasoning Model...`;
      }
      if(selectedTool === 'image_generation') {
        const selectedModel = MODEL_OPTIONS.find((m: ModelConfig) => m.id === chatModeState.selectedModelId);
        const modelName = selectedModel?.displayName.split(' - ')[0] || "Image Generation";
        return `Using ${modelName} Model...`;
      }
      return `Using ${getToolDisplayName(selectedTool)}...`;
    }
    
    if (isMobile) {
      return "Say something...";
    }
    
    // Customize placeholder for different RAG types — only when actually in 3M Knowledge mode
    if (chatModeState.mode === '3M Knowledge') {
      const ragPlaceholder = getRagPlaceholder(chatModeState.ragChatType);
      if (ragPlaceholder) return ragPlaceholder;
    }
    
    return "How can I help you today?";
  };

  // Note: File clearing when switching to 3M Knowledge is now handled by Header.tsx with confirmation dialog

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [confirmationConfig, setConfirmationConfig] = React.useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
  } | null>(null);

  // Mobile tools menu state
  const [isMobileToolsMenuOpen, setIsMobileToolsMenuOpen] = React.useState(false);
  const [isMobileAiModelPanelOpen, setIsMobileAiModelPanelOpen] = React.useState(false);
  // Desktop plus menu state (secondary tools dropdown)
  const [isDesktopPlusMenuOpen, setIsDesktopPlusMenuOpen] = React.useState(false);
  const desktopPlusButtonRef = React.useRef<HTMLButtonElement>(null);
  const [desktopPlusMenuPos, setDesktopPlusMenuPos] = React.useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const [isDesktopAiModelSubPanelOpen, setIsDesktopAiModelSubPanelOpen] = React.useState(false);
  const [desktopAiModelSubPanelPos, setDesktopAiModelSubPanelPos] = React.useState<{ top: number; left: number } | null>(null);
  const desktopAiModelButtonRef = React.useRef<HTMLButtonElement>(null);

  // Tutorial awareness: auto-open plus menu for steps whose targets live inside it
  const tutorialIsActive = useSelector((state: RootState) => state.tutorial.isActive);
  const tutorialCurrentStepId = useSelector((state: RootState) => {
    const { steps, currentStepIndex } = state.tutorial;
    return steps[currentStepIndex]?.id ?? null;
  });
  const isTutorialOnPlusMenuStep = tutorialIsActive && tutorialCurrentStepId !== null && PLUS_MENU_STEP_IDS.has(tutorialCurrentStepId);

  // Open / close the plus menu automatically when the tutorial navigates to / away from plus-menu steps
  React.useEffect(() => {
    if (isTutorialOnPlusMenuStep) {
      if (!isDesktopPlusMenuOpen && desktopPlusButtonRef.current) {
        const rect = desktopPlusButtonRef.current.getBoundingClientRect();
        const dropdownHeight = 140;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < dropdownHeight + 8) {
          setDesktopPlusMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
        } else {
          setDesktopPlusMenuPos({ top: rect.bottom + 6, left: rect.left });
        }
        setIsDesktopPlusMenuOpen(true);
      }
    } else if (tutorialIsActive && isDesktopPlusMenuOpen) {
      // Tutorial moved to a step outside the plus menu — close it
      setIsDesktopPlusMenuOpen(false);
    }
  }, [isTutorialOnPlusMenuStep, tutorialIsActive]); // eslint-disable-line react-hooks/exhaustive-deps
  // Track whether File Upload was selected from '+' menu (pill visible immediately)
  // (removed duplicate declaration)

  // Collapsible file preview state (only for chatPage)
  const [isFilePreviewCollapsed, setIsFilePreviewCollapsed] = React.useState(false);

  // Track if Compare mode is active (visual indicator and auto-filled prompt)
  const [isCompareActive, setIsCompareActive] = React.useState(false);

    // Track if translation has completed to prevent placeholder from being added
  const translationCompletedRef = React.useRef(false);
  const translationFileInputRef = React.useRef<HTMLInputElement>(null);
  // Collapse/expand state for translation mode
  const [isTranslationCollapsed, setIsTranslationCollapsed] = React.useState(false);
  // Track last translation request to detect changes - using Redux for persistence across navigation
  const {lastTranslation, targetLanguages, glossaryFiles, translationHistory} = useSelector((state: RootState) => state.chat);


  // Track tool switching - removed automatic clearing, will be handled with confirmation
  React.useEffect(() => {
    if (!selectedTool || !previousToolRef.current) {
      previousToolRef.current = selectedTool;
      return;
    }
    
    previousToolRef.current = selectedTool;
  }, [selectedTool]);
  // File upload dialog state
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState(false);
  const [hideFileUploadDialogDropzone, setHideFileUploadDialogDropzone] = useState(false);
  const isFileUploadDialogOpenRef = React.useRef(false);
  // Staged files - files selected in the dialog but not yet confirmed with "Upload" button
  type StagedFile = {
    name: string;
    type: string;
    data: string;
    size: number;
    lastModified: number;
    status?: 'pending' | 'uploading' | 'uploaded' | 'error';
    key?: string;
  };
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const stagedFilesRef = React.useRef<StagedFile[]>([]);

  // Device detection using custom hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet } = deviceInfo;
  // Determine if ModelSelector is visible at the top (desktop)
  // This logic must match ChatPage to ensure dropdown and top bar are mutually exclusive.
  // - Use explicit RAG mode check (chatModeState.mode !== '3M Knowledge') instead of
  //   isModelSelectorVisible(chatModeState.mode), which incorrectly interprets mode strings as tool names.
  // - Default isWideScreen to false (not true) so that when the prop is absent the top-bar
  //   is assumed absent, and the AI Model entry always falls back to the plus-menu / mobile sheet.
  const showModelSelector = chatModeState.mode !== '3M Knowledge'
    && !isMobile
    && (isWideScreen ?? false)
    && isModelSelectorVisible(chatModeState.selectedTool, isVoiceModeActive);

  // Auto-focus textarea when streaming ends so user can immediately type next prompt
  const prevIsStreamingRef = React.useRef(isStreaming);
  React.useEffect(() => {
    if (prevIsStreamingRef.current === true && isStreaming === false) {
      // Streaming just ended — focus the textarea (skip on mobile/tablet to avoid keyboard popup)
      if (!isMobile && !isTablet && textareaRef.current) {
        textareaRef.current.focus();
      }
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, isMobile, isTablet]);

  // Auto-collapse translation mode on mobile when chat has messages
  React.useEffect(() => {
    if (selectedTool === 'translation' && translationHistory.length > 0) {
      setIsTranslationCollapsed(true);
    }
  }, [selectedTool, translationHistory.length]);

  // Toggle body class so Gallery tiles become unclickable while dictating
  React.useEffect(() => {
    document.body.classList.toggle('dictation-active', isSpeechListening);
  }, [isSpeechListening]);

  // Handle external value changes (e.g., from gallery tile clicks)
  React.useEffect(() => {
    if (externalValue !== undefined && externalValue !== value && !isSpeechListening) {
      setValue(externalValue);
      // Focus the textarea after setting value
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to end of text
        const length = externalValue.length;
        textareaRef.current.setSelectionRange(length, length);
      }
      // Notify parent that external value was applied
      if (onExternalValueApplied) {
        onExternalValueApplied();
      }
    }
  }, [externalValue, value, onExternalValueApplied, isSpeechListening]);

  // Wrap startSpeech so we snapshot the current textarea value before the
  // session begins. preDictationValueRef is used by the live-preview (value
  // prop) and the processing effect so appending works correctly every time.
  const handleStartSpeech = React.useCallback(() => {
    preDictationValueRef.current = value;
    // Ensure dictation service has MSAL for auth
    dictationService.setMsalInstance(msalInstance);
    startSpeech();
  }, [value, startSpeech, msalInstance]);

  // Whenever the Whisper transcript updates (new chunk transcribed), commit
  // the full accumulated transcript into the textarea.
  React.useEffect(() => {
    if (speechTranscript && !isSpeechListening) {
      setValue((prev) => (prev ? prev + ' ' + speechTranscript : speechTranscript));
      resetSpeech();
      const scrollTimer = setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      }, 80);
      return () => clearTimeout(scrollTimer);
    }
  }, [speechTranscript, isSpeechListening, resetSpeech]);

  // Surface speech recognition errors through the existing warning toast.
  // For mic permission denial, reuse the same MicrophonePermissionDialog that
  // voice mode shows — it has detailed browser instructions (styled dialog).
  React.useEffect(() => {
    if (!speechError) return;
    if (speechError.toLowerCase().includes('microphone access denied') ||
        speechError.toLowerCase().includes('enable microphone permissions')) {
      setShowMicPermissionDialog(true);
    } else {
      console.warn('[Dictation] Transcription error:', speechError);
      setShowDictationError(true);
    }
  }, [speechError, setShowMicPermissionDialog, setShowDictationError]);

  // Helper: choose upload base URL (dev vs prod) with env override
  const resolveUploadEndpoint = React.useCallback(() => {
    const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigatordev.ai.3m.com';
    const envOverride = process.env.REACT_APP_FILE_UPLOAD_URL || undefined;
    const useLocal = process.env.REACT_APP_USE_LOCAL_FILE_UPLOAD === 'true';
    if (envOverride) return envOverride;
    if (useLocal) {
      return 'http://localhost:8006/ai/upload';
    }
    return `${apimBaseUrl}/nav-api/api/ai/file-upload`;
  }, []);

  // Upload a single file (meta passed directly to avoid stale selector race)
  const [uploadError, setUploadError] = useState<{ file: string; message: string } | null>(null);

  // Ensure any global blocking overlay is hidden when an upload error surfaces
  useEffect(() => {
    if (uploadError) {
      dispatch(hideGlobalBlocking());
    }
  }, [uploadError, dispatch]);

  const uploadFilesBatch = React.useCallback(async (fileMetaDirectArray: { name: string; type: string; data: string }[]) => {
    if (!fileMetaDirectArray || fileMetaDirectArray.length === 0) return;
    // Always mark each file as uploading immediately to avoid stale closure issues
    // because state can lag behind the actual upload process (e.g., if user selects files but doesn't confirm upload for a few seconds, the state won't reflect those files yet but we still want to show them as uploading in the UI).
    fileMetaDirectArray.forEach(m => {
      dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploading', errorMessage: undefined }}));
    });

    // Acquire token once
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No authenticated account' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }
    const tokenRequest = { scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`], account: accounts[0] };
    let accessToken: string | undefined;
    try {
      accessToken = (await msalInstance.acquireTokenSilent(tokenRequest)).accessToken;
    } catch (err) {
      try {
        accessToken = (await msalInstance.acquireTokenPopup(tokenRequest)).accessToken;
      } catch (popupErr) {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Token acquisition failed' });
        dispatch(hideGlobalBlocking());
        fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
        return;
      }
    }
    if (!accessToken) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No access token' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    // Build files array with base64 normalization for csv
    const filesPayload = fileMetaDirectArray.map(meta => {
      const looksBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(meta.data.substring(0, 64));
      let contentBase64: string;
      if (meta.type === 'text/csv' && !looksBase64) {
        try {
          contentBase64 = utf8ToBase64(meta.data);
        } catch {
          contentBase64 = btoa(meta.data);
        }
      } else {
        contentBase64 = meta.data;
      }
      return { filename: meta.name, content_base64: contentBase64 };
    });

    const uploadEndpoint = resolveUploadEndpoint();
    const stableSession = sessionId || 'unknown_session';
    const generatedRequestId = `upload_${Date.now()}_${Math.random().toString(36).substring(2,10)}`;
    const generatedTimestamp = new Date().toISOString();
    const requestCore = {
      request_id: generatedRequestId,
      timestamp: generatedTimestamp,
      query_type: 'response-api',
      session: stableSession,
      prompt: 'file-upload-batch',
      metadata: { source: 'react-app' }
    };
    const requestPayload = {
      ...requestCore,
      payload: { ...requestCore },
      files: filesPayload
    };

    // Log sanitized batch payload
    try {
      const sanitized = {
        ...requestPayload,
        files: requestPayload.files.map(f => ({
          filename: f.filename,
          content_base64_head: f.content_base64.slice(0, 60) + (f.content_base64.length > 60 ? '…' : ''),
          content_base64_length: f.content_base64.length
        }))
      };
      logger.log('[upload-batch] request payload', sanitized);
      // ALSO log exact raw JSON string for verification (including full base64 + any future added fields)
      try {
        const rawString = JSON.stringify(requestPayload);
        logger.log('[upload-batch] raw-json-length', rawString.length);
        logger.log('[upload-batch] raw-json-string', rawString);
      } catch {/* ignore serialization issues */}
    } catch {}

    // Retry logic: attempt upload with at least one retry
    let resp: Response | undefined;
    const maxAttempts = 2; // 1 initial attempt + 1 retry
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        logger.log(`[upload-batch] Upload attempt ${attempt}/${maxAttempts}`);
        resp = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestPayload)
        });
        
        // If we got a response, break out of retry loop
        if (resp) {
          logger.log(`[upload-batch] Upload succeeded on attempt ${attempt}`);
          break;
        }
      } catch (e) {
        logger.warn(`[upload-batch] Upload attempt ${attempt} failed:`, e);
        
        // If this isn't the last attempt, wait before retrying
        if (attempt < maxAttempts) {
          const retryDelay = 1000; // 1 second delay before retry
          logger.log(`[upload-batch] Waiting ${retryDelay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    // If all attempts failed, show error
    if (!resp) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'Network error during batch upload after retries' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    try {
      const rawClone = resp.clone();
      const rawText = await rawClone.text();
      const headersObject = Array.from(resp.headers.entries()).reduce((acc: Record<string,string>, [k,v]) => { acc[k]=v; return acc; },{});
      const truncated = rawText.length > 2000 ? rawText.slice(0,2000)+'…(truncated)' : rawText;
      logger.log('[upload-batch] raw response', { status: resp.status, ok: resp.ok, headers: headersObject, body_preview: truncated });
    } catch (err) {
      logger.warn('[upload-batch] failed raw log', err);
    }

    if (!resp.ok) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: `Batch upload failed (HTTP ${resp.status})` });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    let json: any = null;
    try { json = await resp.json(); } catch {}

    try {
      const uploadedFilesArray = json?.data?.content?.uploaded_files;
      const sanitizedResp = json ? {
        top_level_keys: Object.keys(json || {}),
        uploaded_files_mapping: Array.isArray(uploadedFilesArray) ? uploadedFilesArray.map((f:any)=>({ filename: f.filename, file_id: f.file_id, status: f.status })) : undefined,
        source: json?.source
      } : { raw: 'no-json' };
      logger.log('[upload-batch] response', sanitizedResp);
    } catch {}

    const multiUploaded = json?.data?.content?.uploaded_files;
    if (Array.isArray(multiUploaded) && multiUploaded.length > 0) {
      multiUploaded.forEach((f: any) => {
        if (f?.filename) {
          if (f?.file_id && f?.status === 'success') {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'uploaded', fileId: f.file_id } }));
          } else {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'error', errorMessage: f?.status || 'upload_failed' } }));
          }
        }
      });
      // Determine if any still missing fileId
      const missing = multiUploaded.filter((f:any)=>!f.file_id);
      if (missing.length > 0) {
        setUploadError({ file: missing[0].filename || fileMetaDirectArray[0].name, message: 'One or more files missing file_id.' });
      }
      // Only clear spinner if all files are uploaded or error
      const allDone = multiUploaded.every((f:any) => f.file_id || f.status === 'error');
      if (allDone) {
        logger.log('[upload-batch] Upload complete - clearing awaiting states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      } else {
        logger.log('[upload-batch] Some files still uploading, keeping spinner visible');
      }
    } else {
      // Fallback: attempt recursive search for any file_id
      const gatherFileIds: string[] = [];
      const visit = (obj: any, depth = 0) => {
        if (!obj || depth > 6) return;
        if (Array.isArray(obj)) { obj.forEach(el=>visit(el, depth+1)); return; }
        for (const k of Object.keys(obj)) {
          const v = obj[k];
          if (k === 'file_id' && typeof v === 'string') gatherFileIds.push(v);
          if (v && typeof v === 'object') visit(v, depth+1);
        }
      };
      visit(json);
      if (gatherFileIds.length === fileMetaDirectArray.length) {
        fileMetaDirectArray.forEach((m,i)=>{
          dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploaded', fileId: gatherFileIds[i] } }));
        });
        // Only clear spinner if all files are uploaded or error
        const allDone = gatherFileIds.length === fileMetaDirectArray.length;
        if (allDone) {
          logger.log('[upload-batch] Upload complete (fallback path) - clearing awaiting states');
          setAwaitingFileSelection(false);
          filesBeingProcessedRef.current = false;
          dispatch(hideGlobalBlocking());
        } else {
          logger.log('[upload-batch] Some files still uploading (fallback), keeping spinner visible');
        }
      } else {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Batch response did not include uploaded_files array.' });
        dispatch(hideGlobalBlocking());
      }
    }
  }, [msalInstance, dispatch, resolveUploadEndpoint, sessionId]);
  // Ensure we have a persistent session id on mount
  useEffect(() => {
    dispatch(ensureSessionId());
  }, [dispatch]);

  // Prevent browser's default drag-and-drop behavior (opening files in new tab)
  // This ensures files can only be dropped in our designated drop zone
  // Also track when user is dragging a file to disable tool selection
  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleWindowDragEnter = (e: DragEvent) => {
      // Check if dragging files (not text or other data)
      if (e.dataTransfer?.types.includes('Files')) {
        logger.log('[window drag] File drag detected, disabling tool selection');
        setIsFileDragInProgress(true);
      }
    };
    
    const handleWindowDragLeave = (e: DragEvent) => {
      // Only clear if leaving the window entirely (relatedTarget is null)
      if (!e.relatedTarget || e.relatedTarget === null) {
        logger.log('[window drag] File drag left window, re-enabling tool selection after delay');
        // Small delay to handle the gap between drag leave and drop event
        setTimeout(() => {
          setIsFileDragInProgress(false);
        }, 100);
      }
    };
    
    const handleWindowDragEnd = (e: DragEvent) => {
      // Drag was cancelled (Esc key, or drag ended without drop)
      logger.log('[window drag] Drag operation ended/cancelled, re-enabling tool selection');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };
    
    const handleContextMenu = (e: MouseEvent) => {
      // Right-click context menu cancels drag
      logger.log('[window drag] Context menu opened, cancelling drag');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };
    
    const handleEscapeKey = (e: KeyboardEvent) => {
      // Cancel drag if Escape is pressed AND we're currently dragging (use refs to avoid stale closure)
      if (e.key === 'Escape' && (isFileDragInProgressRef.current || isDraggingOverRef.current)) {
        logger.log('[window drag] Escape pressed during drag, cancelling', { 
          isFileDragInProgress: isFileDragInProgressRef.current, 
          isDraggingOver: isDraggingOverRef.current
        });
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
        isFileDragInProgressRef.current = false;
        isDraggingOverRef.current = false;
      }
    };
    
    const handleMouseDown = (e: MouseEvent) => {
      // Right-click cancels drag
      if (e.button === 2) {
        logger.log('[window drag] Right-click detected, cancelling drag');
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
      }
    };

    // Prevent default behavior for drag events on the entire window (bubble phase, not capture)
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);
    window.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('contextmenu', handleContextMenu); // Right-click menu also cancels

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
      window.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Update tool text width based on window size with debouncing (visual only)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateWidth = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setToolTextWidth(40);
      } else if (width < 640) {
        setToolTextWidth(60);
      } else if (width < 768) {
        setToolTextWidth(80);
      } else if (width < 1024) {
        setToolTextWidth(100);
      } else {
        setToolTextWidth(120);
      }
    };

    const debouncedUpdateWidth = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateWidth, 150);
    };

    updateWidth();
    window.addEventListener('resize', debouncedUpdateWidth);
    return () => {
      window.removeEventListener('resize', debouncedUpdateWidth);
      clearTimeout(timeoutId);
    };
  }, []);

  // Track previous height to avoid unnecessary updates
  const previousHeightRef = React.useRef<number>(0);
  const hasReportedHeightRef = React.useRef<boolean>(false); // Track if we've reported a real height

  // Auto-resize textarea
  React.useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Step 1: Reset to base state to get clean content measurement
    // Larger base height for tablets (64px vs 56px)
    const baseHeight = isTablet ? 64 : 56;
    textarea.style.height = baseHeight + 'px';
    textarea.style.paddingBottom = '16px';
    textarea.style.overflowY = 'hidden';
    
    // Step 2: Measure the actual content height
    const scrollHeight = textarea.scrollHeight;
    const contentHeight = Math.max(scrollHeight, baseHeight); // Never less than baseHeight
    const maxContentHeight = 128;
    
    // Step 3: Determine if we need scrolling based on content only
    const needsScrolling = contentHeight > maxContentHeight;
    const finalHeight = needsScrolling ? maxContentHeight : contentHeight;
    
    // Step 4: Apply settings (no extra space needed - bubble is outside textarea)
    textarea.style.height = finalHeight + 'px';
    textarea.style.overflowY = needsScrolling ? 'auto' : 'hidden';

    // Step 5: Auto-scroll to bottom during voice dictation so the user
    // always sees the most recently spoken words as the ghost text grows.
    if (needsScrolling && isSpeechListening) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  // speechTranscript added: during dictation `value` state is frozen (onChange is a
  // no-op while listening) so without this dep the resize never fires as the live
  // preview grows. Adding it triggers a fresh measurement on every new word spoken.
  }, [value, isTablet, speechTranscript, isSpeechListening]);

  // Calculate and report total component height - runs AFTER render
  React.useEffect(() => {
    if (!bottomBubbleContainerRef.current || !onHeightChange) {
      return; // Skip measurement while dropdown is open
    }
    
    // Wait for browser to paint, then measure
    const rafId = requestAnimationFrame(() => {
      if (!bottomBubbleContainerRef.current) return;
      
      const baseHeight = isTablet ? 64 : 56;
      const maxContentHeight = 128;
      
      // Calculate offsets
      const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
      const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
      const GREETING_SPACER = !chatPage ? 36 : 0;
      const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
      
      // Measure the actual height of the bottom bubble container (tools + files + dropdown)
      const bottomBubbleHeight = bottomBubbleContainerRef.current.offsetHeight;
      
      // Calculate finalHeight from textarea if available
      let currentFinalHeight = baseHeight;
      if (textareaRef.current) {
        const currentScrollHeight = textareaRef.current.scrollHeight;
        const currentContentHeight = Math.max(currentScrollHeight, baseHeight);
        const currentNeedsScrolling = currentContentHeight > maxContentHeight;
        currentFinalHeight = currentNeedsScrolling ? maxContentHeight : currentContentHeight;
      }
      
      const totalComponentHeight = currentFinalHeight + bottomBubbleHeight + EXTRA_GALLERY_OFFSET;
      
      // Only update if height changed by more than 10px to prevent measurement fluctuations and feedback loops
      if (Math.abs(totalComponentHeight - previousHeightRef.current) > 10) {
        previousHeightRef.current = totalComponentHeight;
        hasReportedHeightRef.current = true; // Mark that we've reported a real height
        onHeightChange(totalComponentHeight);
      }
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [value, selectedTool, uploadedFiles.length, onHeightChange, chatPage, iconSize, isTablet, isFilePreviewCollapsed, translationHistory.length, isTranslationCollapsed]);

  // Report initial height on mount to ensure padding is correct immediately
  React.useEffect(() => {
    if (!onHeightChange) return;
    
    // Only report initial height if we haven't reported a real height yet
    if (hasReportedHeightRef.current) {
      return;
    }
    
    // Report default height immediately when component mounts
    const baseHeight = isTablet ? 64 : 56;
    const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
    const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
    const GREETING_SPACER = !chatPage ? 36 : 0;
    const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
    
    // Initial height = base textarea + minimal tool bar (approximately 60px)
    const initialHeight = baseHeight + 60 + EXTRA_GALLERY_OFFSET;
    previousHeightRef.current = initialHeight;
    onHeightChange(initialHeight);
  }, [onHeightChange, chatPage, iconSize, isTablet]);


  // Initialize selected tool from initialTools prop, but only if not in RAG mode
  useEffect(() => {
    // Only set initial tool if in Ask Chat mode and not just after a Home navigation
    if (chatModeState.mode === 'Ask Chat' && initialTools && initialTools.length > 0 && !selectedTool && !manuallyClearedToolRef.current) {
      dispatch(setSelectedTool(initialTools[0]));
    }
  }, [initialTools, selectedTool, chatModeState.mode, chatModeState.ragChatType, dispatch]);

  // Set firstToolUsed when initialTools is provided and conversation starts (chatPage mode)
  useEffect(() => {
    if (chatPage && initialTools && initialTools.length > 0 && !firstToolUsed) {
      setFirstToolUsed(initialTools[0]);
    }
  }, [chatPage, initialTools, firstToolUsed]);


  // Clear selected tool when switching RAG chat types
  useEffect(() => {
    if (chatModeState.ragChatType) {
      // When switching to a RAG type, clear any selected tool so the placeholder updates
      dispatch(setSelectedTool(null));
    }
  }, [chatModeState.ragChatType, dispatch]);

  // Dropdown toggle removed.

  const chatOptions: ChatOption[] = React.useMemo(() => [
    // Removed standalone File Upload tool – uploads now handled either in general chat (no selected tool) or data_analysis.
    { label: 'Web Search', icon: TbWorldSearch, apiLabel: 'web_search' },
    { label: 'Reasoning Model', icon: ReasoningModelIcon, apiLabel: 'reasoning' },
    { label: 'Create Image', icon: PiImage, apiLabel: 'image_generation' },
    { label: 'Data Analysis', icon: DataAnalysisIcon, apiLabel: 'data_analysis' },
    { label: 'Document Translation', icon: MdTranslate, apiLabel: 'translation' },
  ], []);

  const handleModelSelection = React.useCallback((modelId: string) => {
    dispatch(setSelectedTool(getToolForModelSelection(modelId)));
    dispatch(setSelectedModel(modelId));
  }, [dispatch]);

  const effectiveToolForSend = selectedTool ?? getToolForModelSelection(chatModeState.selectedModelId);

  React.useEffect(() => {
    if (effectiveToolForSend === 'image_generation' && isFileUploadActive) {
      setIsFileUploadActive(false);
    }
  }, [effectiveToolForSend, isFileUploadActive]);

  // File upload is not available when the active/effective tool is image generation
  const isFileUploadSupported = effectiveToolForSend !== 'image_generation';

  const handleOptionClick = (apiLabel: string, event?: React.MouseEvent | React.MouseEvent<HTMLButtonElement> & { nativeEvent?: any }) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Check if we have uploaded files and are switching to a different tool (or selecting a tool when none selected)
    if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
      const hasImages = uploadedFiles.some(f => 
        /image\/(png|jpeg|jpg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)
      );
      const hasDataFiles = uploadedFiles.some(f => 
        /text\/csv/.test(f.type) || /application\/vnd\.(ms-excel|openxmlformats-officedocument\.spreadsheetml\.sheet)/.test(f.type) ||
        /\.(csv|xls|xlsx)$/i.test(f.name)
      );
      const hasTextFiles = uploadedFiles.some(f => 
        /application\/(pdf|msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document|vnd\.(ms-powerpoint|openxmlformats-officedocument\.presentationml\.presentation))/.test(f.type) ||
        /\.(pdf|doc|docx|ppt|pptx|txt)$/i.test(f.name)
      );
      
      let shouldWarn = false;
      let warningMessage = '';
      
      // Check for incompatible tool selections based on file types
      if (apiLabel === 'reasoning' && (hasImages || hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'Reasoning mode does not support uploaded files. Switching to this mode will clear your chat history and remove all uploaded files. Are you sure you want to continue?';
      } else if (apiLabel === 'image_generation' && (hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Image Generation tool only supports image files. Switching to this tool will clear your chat history and remove all incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'web_search' && hasDataFiles) {
        shouldWarn = true;
        warningMessage = 'The Web Search tool does not support data analysis files (CSV/Excel). Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'data_analysis' && (hasImages || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Data Analysis tool only supports CSV and Excel files. Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
        // Even if files are compatible, warn when switching tools with files uploaded
        shouldWarn = true;
        warningMessage = 'Switching tools will clear your chat history and all uploaded files. Are you sure you want to continue?';
      } else if (selectedTool === 'translation' && apiLabel !== 'translation' && (hasTextFiles || hasDataFiles)) {
        shouldWarn = true;
        warningMessage = 'The Document Translation tool only supports document files (PDF, Word, PowerPoint, Excel). Switching to another tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      }
      
      // If we should warn, show confirmation dialog
      if (shouldWarn) {
        setConfirmationConfig({
          title: 'Switch Tool?',
          message: warningMessage,
          onConfirm: () => {
            // Clear history and files
            if (onClearChat) {
              onClearChat();
            }
            dispatch(clearUploadedFiles());
            // Also clear the ref immediately so proceedWithToolSelection doesn't read stale
            // file data before the Redux dispatch triggers a re-render.
            uploadedFilesRef.current = [];
            setShowConfirmation(false);
            setConfirmationConfig(null);
            // Now set the new tool
            proceedWithToolSelection(apiLabel);
            dispatch(clearTranslationData());
            setIsTranslationCollapsed(false);
          }
        });
        setShowConfirmation(true);
        // Don't proceed - keep existing tool if user cancels
        return;
      }
    }
    
    // Check if switching from a model-eligible context (standard chat, reasoning, web_search)
    // to a non-model-eligible tool (image_generation, data_analysis, translation).
    // These tools don't support model selection, so previous chat history must be cleared
    // to avoid stale model/queryType leaking into the request payload.
    const isCurrentModelEligible = !selectedTool || MODEL_ELIGIBLE_TOOLS.has(selectedTool);
    const isTargetModelEligible = MODEL_ELIGIBLE_TOOLS.has(apiLabel);
    if (isCurrentModelEligible && !isTargetModelEligible && hasMessages && uploadedFiles.length === 0) {
      setConfirmationConfig({
        title: 'Clear Chat History?',
        message: 'This tool requires a fresh conversation. Your current chat history will be cleared. Are you sure you want to continue?',
        onConfirm: () => {
          if (onClearChat) {
            onClearChat();
          }
          setShowConfirmation(false);
          setConfirmationConfig(null);
          proceedWithToolSelection(apiLabel);
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
        }
      });
      setShowConfirmation(true);
      return;
    }

    // Check if switching from one tool to another (when there are messages but no files)
    if (selectedTool && selectedTool !== apiLabel && hasMessages && uploadedFiles.length === 0 && shouldClearForTargetTool(apiLabel)) {
      // Show confirmation dialog for tool switching only if there are messages
      setConfirmationConfig({
        title: 'Switch Tool?',
        message: 'Switching tools will clear your chat history. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history
          if (onClearChat) {
            onClearChat();
          }
          setShowConfirmation(false);
          setConfirmationConfig(null);
          // Now set the new tool
          proceedWithToolSelection(apiLabel);
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
        }
      });
      setShowConfirmation(true);
      return;
    }
    
    // No confirmation needed, proceed directly
    proceedWithToolSelection(apiLabel);
  };

  const proceedWithToolSelection = React.useCallback((apiLabel: string) => {
    // Close mobile menu if open
    if (isMobile) {
      setIsMobileToolsMenuOpen(false);
    }
    dispatch(setSelectedTool(apiLabel));

    // Hide the File Upload pill whenever a named secondary tool becomes active
    if (apiLabel === 'data_analysis' || apiLabel === 'translation') {
      setIsFileUploadActive(false);
    }

    if (apiLabel === 'data_analysis') {
      // When activating data analysis, evaluate existing files.
      const isValidAnalysisType = (f: any) => {
        const t = (f.type || '').toLowerCase();
        const n = (f.name || '').toLowerCase();
        return t === 'text/csv' || t === 'application/vnd.ms-excel' || t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.csv$/i.test(n) || /\.(xls|xlsx)$/i.test(n);
      };
      const existing = [...uploadedFilesRef.current];
      const valid = existing.filter(isValidAnalysisType);
      const invalid = existing.filter(f => !isValidAnalysisType(f));
      // Remove invalids with warning alert if any
      if (invalid.length > 0) {
        invalid.forEach(f => dispatch(removeUploadedFile(f.name)));
        // Provide filenames for transparency (truncate if many)
        const names = invalid.map(f=>f.name).slice(0,5);
        const more = invalid.length > names.length ? ` +${invalid.length - names.length} more` : '';
        setFileWarning({ message: `Removed ${invalid.length} invalid file(s): ${names.join(', ')}${more}. Only CSV or Excel (max 2, <16MB each) are allowed for Data Analysis.`, mode: 'general' });
      }
      // If we have valid files already, upload any that lack a fileId
      if (valid.length > 0) {
        const toUpload = valid.filter(f => !f.fileId).map(f => ({ name: f.name, type: f.type, data: f.data }));
        if (toUpload.length > 0) {
          uploadFilesBatch(toUpload).finally(() => {
            dispatch(hideGlobalBlocking());
          });
        }
        // Do NOT trigger file picker since at least one valid file exists.
      } else if (invalid.length === 0) {
        // No valid files and no invalid removal warning -> open FileUploadDialog immediately
        setIsFileUploadDialogOpen(true);
      } else {
        // Invalid removal warning is showing; defer opening dialog until user clicks OK
        // We rely on fileWarning OK handler to detect zero valid files and open dialog then.
      }
    }
    
    if (apiLabel === 'translation') {
       const willNavigate = !chatPage && onNavigateToChat;
      if (willNavigate) {
        onNavigateToChat('', ['translation']);
      }
    }
    
    // Focus textarea after tool selection - but not on mobile/tablet to avoid triggering keyboard
    if (!isMobile && !isTablet) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isMobile, isTablet, dispatch, uploadFilesBatch, chatPage, onNavigateToChat,
      setIsMobileToolsMenuOpen, setFileWarning, setIsFileUploadDialogOpen]);

  const handleRemoveTool = () => {
    const shouldClearChat = shouldClearForTargetTool(null);

    // Show confirmation dialog for tool removal only if there are messages and the destination actually changes conversation context
    if (hasMessages && shouldClearChat) {
      setConfirmationConfig({
        title: 'Remove Tool?',
        message: 'Removing this tool will clear your chat history and all uploaded files. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history and reset to home state when tool is removed
          if (onClearChat) {
            onClearChat();
          }
          
          manuallyClearedToolRef.current = true;
          dispatch(setSelectedTool(null));
          setAwaitingFileSelection(false);
          dispatch(clearUploadedFiles());
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
          dispatch(hideGlobalBlocking());
          setShowConfirmation(false);
          setConfirmationConfig(null);
        }
      });
      setShowConfirmation(true);
    } else {
      // No confirmation needed if there are no messages or the conversation already belongs to regular chat.
      if (shouldClearChat && onClearChat) {
        onClearChat();
      }
      manuallyClearedToolRef.current = true;
      dispatch(setSelectedTool(null));
      setAwaitingFileSelection(false);
      dispatch(clearUploadedFiles());
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
      dispatch(hideGlobalBlocking());
    }
  };
  
  const getToolDisplayName = (apiLabel: string) => {
    const option = chatOptions.find(opt => opt.apiLabel === apiLabel);
    return option?.label || apiLabel;
  };

  const triggerAdditionalUpload = React.useCallback(() => {
    // Hide any existing global blocking overlay before showing dialog
    dispatch(hideGlobalBlocking());
    // Clear any previously selected tool — file upload from the plus menu is general-chat BYOD,
    // so no tool name should be sent in the payload.
    dispatch(setSelectedTool(null));
    manuallyClearedToolRef.current = true;
    // Track which files exist before opening dialog (so we know which ones to keep/remove)
    setStagedFiles([]);
    // Show the custom file upload dialog instead of directly triggering file picker
    setIsFileUploadDialogOpen(true);
  }, [dispatch]);

  // Listen for gallery tool selection events
  useEffect(() => {
    const handleGalleryToolSelection = (event: Event) => {
      if (isSpeechListening) return;
      const customEvent = event as CustomEvent<{ tool: string }>;
      const toolName = customEvent.detail.tool;
      
      logger.log('[ChatInput] Gallery tool selection event received:', toolName);
      
      // Map gallery tool names to API labels
      const toolMapping: { [key: string]: string } = {
        'image_generation': 'image_generation',
        'web_search': 'web_search',
        'reasoning': 'reasoning',
        'data_analysis': 'data_analysis',
        'upload_file': 'upload_file', // For general file upload
        'none': 'none', // Explicitly disable any selected tool
        'translation': 'translation'
      };
      
      const apiLabel = toolMapping[toolName];
      
      logger.log('[ChatInput] Mapped to apiLabel:', apiLabel);
      
      if (apiLabel === 'upload_file') {
        // Trigger file upload dialog for general chat (BYOD) - same as clicking BYOD button
        logger.log('[ChatInput] Triggering file upload dialog');
        triggerAdditionalUpload();
      } else if (apiLabel === 'none') {
        // Disable any currently selected tool (switch to general chat mode)
        logger.log('[ChatInput] Clearing tool selection');
        manuallyClearedToolRef.current = true;
        dispatch(setSelectedTool(null));
        dispatch(clearTargetLanguages());
      } else if (apiLabel && apiLabel !== '') {
        // Directly call proceedWithToolSelection to bypass confirmation dialogs
        // Gallery tiles should act as direct tool selection
        logger.log('[ChatInput] Proceeding with tool selection:', apiLabel);
        proceedWithToolSelection(apiLabel);
      }
    };
    
    window.addEventListener('galleryToolSelection', handleGalleryToolSelection);
    return () => window.removeEventListener('galleryToolSelection', handleGalleryToolSelection);
  }, [dispatch, proceedWithToolSelection, triggerAdditionalUpload, isSpeechListening]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = value.trim();

    // DATA ANALYSIS GATING: When the data_analysis tool is active we require at least one uploaded file
    // before allowing a message to be sent. This guarantees the backend receives contextual data to analyze.
    if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;

    // Track first tool used when conversation starts
    if (!firstToolUsed && effectiveToolForSend) {
      setFirstToolUsed(effectiveToolForSend);
    }

    // If Compare is active and exactly 2 files, send with no tool (not as a tool)
    if (isCompareActive && uploadedFiles.length === 2) {
      setIsCompareActive(false); // Reset after comparison
      onNavigateToChat(trimmedValue);
      setValue('');
      return;
    }

      // For all other cases, send with the effective tool so model-selected reasoning mode
      // still includes ['reasoning'] even when no explicit tool pill is selected.
      const tools = effectiveToolForSend ? [effectiveToolForSend] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    setIsSummarizeActive(false);
    // Don't clear selectedTool - keep it selected for next message
  };

  const handleSendClick = () => {
    const trimmedValue = value.trim();
    
    // Compare mode
    if (isCompareActive && uploadedFiles.length === 2) {
      setIsCompareActive(false); // Reset after comparison
    }
    
    // DATA ANALYSIS GATING (manual send button / click path)
  if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;
    
    // Track first tool used when conversation starts
    if (!firstToolUsed && effectiveToolForSend) {
      setFirstToolUsed(effectiveToolForSend);
    }
    
    // Send with selected tool (if any)
    const tools = effectiveToolForSend ? [effectiveToolForSend] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    setIsSummarizeActive(false);
    // Don't clear selectedTool - keep it selected for next message
  };

  const handleTranslateDocuments = async () => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      logger.error('[translate] No files to translate');
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      logger.error('[translate] No target languages selected');
      return;
    }

    // Check if this is a duplicate translation (same files + languages)
    const currentFileNames = uploadedFiles.map(f => f.name).sort();
    const currentLanguageCodes = targetLanguages.map(l => l.languageCode).sort();
    const currentGlossaryIDs = (glossaryFiles || []).map(g => `${g.lang}:${g.file.filename}`).sort();
    const user = auth.profile?.account?.username;
    
    // Compare with last translation to detect duplicates or incremental changes
    const filesAreSame = lastTranslation && 
      JSON.stringify(lastTranslation.fileNames) === JSON.stringify(currentFileNames);

    const glossariesAreSame = lastTranslation &&
      JSON.stringify(lastTranslation.glossaryFiles || []) === JSON.stringify(currentGlossaryIDs);
    
    const isDuplicate = filesAreSame && glossariesAreSame &&
      JSON.stringify(lastTranslation.languageCodes) === JSON.stringify(currentLanguageCodes);
    
    if (isDuplicate) {
      logger.warn('[translate] Duplicate translation request - same files, glossaries and languages');
      return;
    }

    // Determine if we should do incremental translation (same files + glossaries, new languages)
    let languagesToTranslate: ILanguageOption[];
    let isIncrementalTranslation = false;
    
    if (filesAreSame && glossariesAreSame && lastTranslation) {
      // Files are the same, check if there are new languages
      const previousLanguageCodes = new Set(lastTranslation.languageCodes);
      const newLanguages = targetLanguages.filter(
        lang => !previousLanguageCodes.has(lang.languageCode)
      );
      
      if (newLanguages.length > 0) {
        // We have new languages to translate
        languagesToTranslate = newLanguages;
        isIncrementalTranslation = true;
        logger.info('[translate] Incremental translation - same files, new languages', {
          previousLanguages: lastTranslation.languageCodes,
          newLanguages: newLanguages.map(l => l.languageCode)
        });
      } else {
        // No new languages, this shouldn't happen due to isDuplicate check above
        logger.warn('[translate] No new languages found');
        return;
      }
    } else {
      // Files changed or no previous translation - translate all languages
      languagesToTranslate = targetLanguages;
      isIncrementalTranslation = false;
    }

    // Update last translation tracking in Redux
    dispatch(setLastTranslation({
      fileNames: currentFileNames,
      languageCodes: currentLanguageCodes,
      glossaryFiles: currentGlossaryIDs
    }));

    try {
      translationCompletedRef.current = false;

      logger.info('[translate] Starting translation', {
        fileCount: uploadedFiles.length,
        languages: languagesToTranslate.map(l => l.languageCode),
        isIncremental: isIncrementalTranslation
      });

      const messageContent = isIncrementalTranslation
          ? `Translate the attached document(s) to additional languages: ${languagesToTranslate.map(l => l.label).join(', ')}`
          : `Translate the attached document(s) to: ${languagesToTranslate.map(l => l.label).join(', ')}`;
        
      const userMessage = {
          id: `translate-user-${Date.now()}`,
          role: 'user',
          content: buildTranslationMessageContent(uploadedFiles, messageContent),
          timestamp: new Date().toISOString(),
      };
      
      dispatch(setIsTranslating(true));

      // Always append to history to keep a log of all translations in the session
      if (translationHistory.length > 0) {
        dispatch(setTranslationHistory([...translationHistory, userMessage]));
      } else {
        dispatch(setTranslationHistory([userMessage])); 
      }

      // Convert uploaded files to File objects
      const files: File[] = await Promise.all(
        uploadedFiles.map(async (uploadedFile) => {
          const mimeType = uploadedFile.type || 'application/octet-stream';
          const storedData = uploadedFile.data;

          // CSV and other text-based files are stored as plain text (readAsText), not base64.
          // Binary files (docx, xlsx, pdf, etc.) are stored as base64 (readAsArrayBuffer).
          const isTextBased = /\.csv$/i.test(uploadedFile.name) || mimeType === 'text/csv' || mimeType === 'text/plain';

          let blob: Blob;
          if (isTextBased) {
            // Plain text — create blob directly from the string
            blob = new Blob([storedData], { type: mimeType });
          } else {
            // Base64-encoded binary — decode to binary first
            const byteCharacters = atob(storedData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
          }

          return new File([blob], uploadedFile.name, { type: mimeType });
        })
      );

      // Create translation service instance
      const translationService = createDocumentTranslationService(msalInstance);
      // Get language codes array (only new languages if incremental)
      const targetLanguageCodes = languagesToTranslate.map(lang => lang.languageCode);

      // Call translation service
      const result = await translationService.translateDocuments(
        files,
        targetLanguageCodes,
        glossaryFiles,
        user,
        sessionId,
        (payload) => dispatch(setTranslationQuota(payload)) // Update quota in Redux as we receive it
      );

      logger.info('[translate] Translation completed', { result });
      
      // Log the full result structure for debugging
      logger.log('[translate] Full response:', JSON.stringify(result, null, 2));
      logger.log('[translate] Response keys:', Object.keys(result));
      logger.log('[translate] Result type:', result.type);
      logger.log('[translate] Result data:', result.data);

      // Extract translated files from nested structure
      // The response can be either:
      // 1. Direct format: { translated_files: [...], error_message: "..." }
      // 2. Nested format: { data: { content: { translated_files: [...] } } }
      // 3. Streaming format: { type: "content", data: { content: { translated_files: [...] } } }
      let translatedFiles = result.translated_files || 
                           result.data?.content?.translated_files ||
                           result.content?.translated_files ||
                           [];
      
      const errorMessage = result.error_message || 
                          result.data?.content?.error_message || 
                          result.content?.error_message ||
                          result.error_data?.message;

      logger.log('[translate] Extracted translated files:', translatedFiles);
      logger.log('[translate] Extracted error message:', errorMessage);

      // Add assistant message to chat with download buttons
      if (translatedFiles && translatedFiles.length > 0) {
        logger.info('[translate] Translation completed successfully', {
          fileCount: translatedFiles.length,
          isIncremental: isIncrementalTranslation
        });
        
        // Prepare translation data for the message
        const translationData = translatedFiles.map((f: any) => ({
          filename: f.filename,
          content_base64: f.content_base64,
          language: f.language || f.language_name
        }));
        
        logger.log('[translate] Prepared translationData:', translationData);
        logger.log('[translate] translationData length:', translationData.length);
        logger.log('[translate] translationData[0]:', translationData[0]);
        
        const successMessage = isIncrementalTranslation
          ? `Additional translations completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`
          : `Translation completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`;
        
        const assistantMessage = {
          id: `translate-assistant-${Date.now()}`,
          timestamp: new Date().toISOString(),
          role: 'assistant',
          content: successMessage,
          translationData // Store translation data for download buttons
        };

        // Use addTranslationMessage (appends in the reducer) instead of spreading the
        // stale-closure copy of translationHistory — the async gap between the user-message
        // dispatch and this point means translationHistory captured in this closure is
        // no longer current.
        dispatch(addTranslationMessage(assistantMessage));
        
        logger.log('[translate] Created assistant message with translationData');
        logger.log('[translate] assistantMessage.translationData:', assistantMessage.translationData);
        logger.log('[translate] Full assistantMessage:', JSON.stringify(assistantMessage, null, 2));
        
        // Mark translation as completed to prevent placeholder from being added
        translationCompletedRef.current = true;
      } else {
        // Unknown response format
        logger.warn('[translate] No translated_files in response', { 
          resultKeys: Object.keys(result),
          fullResult: result 
        });
        logger.error('[translate] Unexpected response format - no translated_files or error_message');
      }
    } catch (error) {
      logger.error('[translate] Translation error', { error });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Add error message to history instead of using transient error state
      // This ensures the error persists in the conversation flow
      const errorMessage = {
        id: `translate-error-${Date.now()}`,
        role: 'assistant',
        content: `${errorMsg}`,
        error: errorMsg,
        timestamp: new Date().toISOString()
      };
      
      dispatch(addTranslationMessage(errorMessage));
      dispatch(setTranslationError('')); // Clear transient error
    } finally {
      dispatch(setIsTranslating(false));
    }
  };

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Block submission while streaming — user can still type but not send
      if (isStreaming) return;
      const form = e.currentTarget.closest('form');
      if (form) {
        // DATA ANALYSIS GATING (keyboard Enter path)
  if (!(selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0)) {
          form.requestSubmit();
        }
      }
    }
    
    // Quick tool shortcuts (Ctrl/Cmd + number)
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      const shortcuts: { [key: string]: string } = {
        // Removed shortcut 1 (file_upload) since uploads handled contextually.
        '2': 'web_search', 
        '3': 'image_generation',
        '4': 'data_analysis',
        '5': 'translation'
      };
      
      if (shortcuts[e.key]) {
        e.preventDefault();
        dispatch(setSelectedTool(shortcuts[e.key]));
      }
    }
    
    // Escape to clear tool selection
    if (e.key === 'Escape' && selectedTool) {
      e.preventDefault();
      dispatch(setSelectedTool(null));
      dispatch(clearTargetLanguages());
    }

    // Ctrl+Shift+D — Toggle dictation
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      if (isSpeechListening) {
        if (dictationMode === 'batch') {
          confirmDictation();
        } else {
          stopSpeech();
        }
      } else if (!isStreaming && !isSpeechTranscribing && !isSpeechStarting) {
        handleStartSpeech();
      }
    }
  }, [selectedTool, uploadedFiles, dispatch, isStreaming, isSpeechListening, isSpeechTranscribing, isSpeechStarting, dictationMode, confirmDictation, stopSpeech, handleStartSpeech]);
  // Memoize cursor color to prevent recalculation
  const cursorColor = React.useMemo(() => {
    if (accent !== 'none') {
      return 'var(--color-accent)';
    }
    
    // For 'none' accent, force visible cursor colors based on theme mode
    if (typeof window !== 'undefined') {
      const htmlElement = document.documentElement;
      const theme = htmlElement.getAttribute('data-theme') || '';
      
      // Check if it's a dark theme
      if (theme.startsWith('dark-')) {
        return '#9ca3af'; // Much lighter gray for dark themes (gray-400)
      } else {
        return '#374151'; // Dark gray for light themes (gray-700)
      }
    }
    
    // Fallback - use a neutral visible color
    return '#6b7280'; // gray-500 as fallback
  }, [accent]);

  // Memoize textarea styles to prevent object recreation
  const textareaStyles = React.useMemo(() => ({
    scrollbarWidth: 'thin' as const,
    scrollbarColor: '#9CA3AF transparent',
    WebkitScrollbarWidth: '6px',
    fontSize: 'var(--app-font-size)', // Respects user font size preference from theme settings
    lineHeight: '24px',
    paddingTop: (isMobile) ? '8px' : isTablet ? '10px' : '12px',
    paddingBottom: '12px', // Always use base padding - no extra space for tool
    paddingLeft: '16px', // Reduced left padding (removed plus button)
  // Increase right padding so user text never flows beneath the right-side icon cluster.
  // After redesign: only 2 icons on the right (dictate + voice-agent/send).
  // Each icon button ≈ 40px.
  //   Ask Chat, Cancel loading          : Cancel(~156px) + mic(~40px)                → 200px
  //   Ask Chat, speech supported        : mic(~40px) + voice/send(~40px)             → 100px
  //   Ask Chat, no STT / voice active   : voice/send(~40px)                          →  60px
  //   3M Knowledge, speech supported    : mic(~40px) + send(~40px)                   → 100px
  //   3M Knowledge, no STT              : send(~40px)                                →  60px
    paddingRight: voiceModeLoading
      ? '200px'                                                    // Cancel + mic
      : !isVoiceModeActive
        ? '100px'                                                  // mic + voice/send
        : '60px',                                                  // voice/send only
    caretColor: cursorColor,
    verticalAlign: 'top' as const,
    boxShadow: 'none', // Always remove shadow to eliminate seam above bubbles
    transition: 'border-color 0.2s ease-in-out, outline 0.2s ease-in-out, padding-right 0.2s ease-in-out',
  } as React.CSSProperties & {
    WebkitScrollbarWidth?: string;
  }), [cursorColor, isMobile, isTablet, voiceModeLoading, isVoiceModeActive]);

    // Cleanup function for cancel detection
  const cleanupCancel = React.useCallback(() => {
    logger.log('[cleanupCancel] Called - Current state:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection,
      selectedTool
    });
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setShowImmediateSpinner(false);
    dispatch(hideGlobalBlocking());
    // Do NOT clear data_analysis tool when OS file picker is cancelled
    // Tool should only be disabled when FileUploadDialog is explicitly cancelled
    logger.log('[cleanupCancel] Cleanup complete');
  }, [dispatch, selectedTool, awaitingFileSelection]);

  useFileInputCancelListener(fileInputRef, cleanupCancel);

  // Window focus listener for cancel detection
  // Check if file input value changed - this is INSTANT and synchronous
  useEffect(() => {
    // Sync ref with state whenever state changes
    awaitingFileSelectionRef.current = awaitingFileSelection;
  }, [awaitingFileSelection]);

  useEffect(() => {
    if (showImmediateSpinner && !awaitingFileSelection) {
      setShowImmediateSpinner(false);
      setIsDraggingOver(false);
      directDropBatchFileNamesRef.current = [];
      isDirectDropBatchActiveRef.current = false;
    }
  }, [showImmediateSpinner, awaitingFileSelection]);

  useEffect(() => {
    if (
      isFileUploadDialogOpen &&
      hideFileUploadDialogDropzone &&
      !awaitingFileSelection &&
      stagedFiles.length > 0 &&
      stagedFiles.every(file => file.status !== 'uploading')
    ) {
      setHideFileUploadDialogDropzone(false);
    }
  }, [isFileUploadDialogOpen, hideFileUploadDialogDropzone, awaitingFileSelection, stagedFiles]);
  
  // Sync drag state refs with state for event handlers
  useEffect(() => {
    isFileDragInProgressRef.current = isFileDragInProgress;
  }, [isFileDragInProgress]);
  
  useEffect(() => {
    isDraggingOverRef.current = isDraggingOver;
  }, [isDraggingOver]);

  // Sync dialog/staged refs so handleFileChange closure always reads the latest values
  useEffect(() => {
    isFileUploadDialogOpenRef.current = isFileUploadDialogOpen;
  }, [isFileUploadDialogOpen]);

  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);

  useEffect(() => {
    const handleFocus = () => {      
      // Check the ref value (current at time of focus event)
      if (!awaitingFileSelectionRef.current) {
        logger.log('[window focus] Not awaiting file selection, ignoring');
        return;
      }
      
      // INSTANT CHECK: Did the file input value change?
      // If value is still empty, user cancelled. If value exists, files were selected.
      // Check both the regular file input and the translation-specific file input,
      // because translation mode uses translationFileInputRef (not fileInputRef) to pick files.
      const hasValue = (fileInputRef.current?.value && fileInputRef.current.value !== '')
        || (translationFileInputRef.current?.value && translationFileInputRef.current.value !== '');
      
      if (!hasValue) {
        // No value = user cancelled
        cleanupCancel();
      } else {
        // Files were selected — remove the full-screen blocking overlay immediately so
        // the user can interact with the UI (e.g. click "Click to cancel") while files
        // are being read. awaitingFileSelection stays true until processing finishes.
        dispatch(hideGlobalBlocking());
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [cleanupCancel,dispatch]); // cleanupCancel dependency needed for timeout callback

    // File category helpers
    const isImageFile = (f: File) => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type);
    const isDataFile = (f: File) => /\.csv$/i.test(f.name) || f.type === 'text/csv' || f.type === 'application/vnd.ms-excel' || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.(xls|xlsx)$/i.test(f.name);
    const isTextFile = (f: File) => {
      return /\.pdf$/i.test(f.name) || f.type === 'application/pdf' ||
             /\.docx?$/i.test(f.name) || f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || f.type === 'application/msword' ||
             /\.pptx?$/i.test(f.name) || f.type === 'application/vnd.ms-powerpoint' || f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
             /\.msg$/i.test(f.name) || f.type === 'application/vnd.ms-outlook';
    };

    const isImageFileType = (fileType: string) => {
      return fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg';
    }
    const isDataFileType = (fileType: string) => {
      return fileType === 'text/csv' || fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    const isTextDocumentType = (fileType: string) => {
      return fileType === 'application/pdf' || 
             fileType === 'application/msword' || 
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             fileType === 'text/plain' ||
             fileType === 'application/vnd.ms-powerpoint' ||
             fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
             fileType === 'application/vnd.ms-outlook';
    }

    
    // Categorize existing and incoming files
    const getFileCategory = React.useCallback((f: File) => {
      if (isImageFile(f)) return 'image';
      if (isDataFile(f)) return 'data';
      if (isTextFile(f)) return 'text';
      return 'unknown';
    }, []);

    // Determine allowed file types based on existing uploaded files
    const getAllowedFileTypes = React.useMemo(() => {
      // If data analysis mode is active, only allow data files
      if (selectedTool === 'data_analysis') {
        return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      if (selectedTool === 'translation') {
        return '.txt,.html,.csv,.docx,.pptx,.pdf,.msg,.xlsx,text/plain,text/html,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-outlook';
      }

      // If no files uploaded yet, allow all types
      if (uploadedFiles.length === 0) {
        return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
      }

      // Determine the category of existing files
      const existingCategories = new Set(uploadedFiles.map(f => {
        const name = f.name;
        const type = f.type;
        const mockFile = { name, type } as File;
        return getFileCategory(mockFile);
      }));

      // If we have mixed categories or unknown, fallback to all types (shouldn't happen but safe)
      if (existingCategories.size > 1 || existingCategories.has('unknown')) {
        return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
      }

      // Get the single category
      const category = Array.from(existingCategories)[0];

      // Return accept string based on category
      if (category === 'image') {
        return '.png,.jpg,.jpeg,image/png,image/jpeg';
      } else if (category === 'data') {
        return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (category === 'text') {
        return '.ppt,.pptx,.pdf,.txt,.doc,.docx,.msg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,application/vnd.ms-outlook';
      }

      // Fallback to all types
      return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
    }, [selectedTool, uploadedFiles, getFileCategory]);


  // ---------------------------------------------------------------------------
  // Shared file-limit helper — used by both handleFileChange and handleDrop.
  // Returns computed limits, the effective (possibly truncated) file list, and
  // a ready-to-use warning object when limits are breached.
  // ---------------------------------------------------------------------------
  const computeFileLimits = React.useCallback((
    incomingFiles: File[],
    existingCount: number,
    opts: { isAnalysisMode: boolean; isTranslationMode: boolean; allImagesCombined: boolean }
  ): {
    maxTotal: number;
    remainingSlots: number;
    hardLimitReached: boolean;
    effectiveFiles: File[];
    warning: { title: string; subtitle?: string | null; message: string; mode: 'general' | 'translation' } | null;
  } => {
    const { isAnalysisMode, isTranslationMode, allImagesCombined } = opts;
    const maxTotal = isTranslationMode ? 5 : isAnalysisMode ? 2 : (allImagesCombined ? 5 : 2);
    const remainingSlots = maxTotal - existingCount;

    if (remainingSlots <= 0) {
      const warning = isTranslationMode
        ? { title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.', message: 'You have reached the maximum of 5 files for translation. Remove a file to add another.', mode: 'translation' as const }
        : { title: 'Maximum Files Reached', message: isAnalysisMode ? 'Maximum of 2 analysis files reached. Remove one to add another.' : allImagesCombined ? 'Maximum of 5 image files reached. Remove one to add another.' : 'Maximum of 2 files reached. Remove one to add another.', mode: 'general' as const };
      return { maxTotal, remainingSlots: 0, hardLimitReached: true, effectiveFiles: [], warning };
    }

    if (incomingFiles.length > remainingSlots) {
      const keptList = incomingFiles.slice(0, remainingSlots).map(f => `- ${f.name}`).join('\n');
      const discardedList = incomingFiles.slice(remainingSlots).map(f => `- ${f.name}`).join('\n');
      const warning = isTranslationMode
        ? { title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.', message: `Only ${maxTotal} files are allowed for translation.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`, mode: 'translation' as const }
        : { title: 'Maximum Files Reached', message: isAnalysisMode
            ? `Only ${maxTotal} files are allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
            : allImagesCombined
              ? `Only ${maxTotal} image files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
              : `Only ${maxTotal} files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`,
          mode: 'general' as const };
      return { maxTotal, remainingSlots, hardLimitReached: false, effectiveFiles: incomingFiles.slice(0, remainingSlots), warning };
    }

    return { maxTotal, remainingSlots, hardLimitReached: false, effectiveFiles: incomingFiles, warning: null };
  }, []);

  // Handle file selection
  const handleFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('[handleFileChange] Function entered', { timestamp: performance.now() });
    
    const filesList = Array.from(e.target.files || []);
    
    logger.log('[handleFileChange] Files array created', { fileCount: filesList.length, timestamp: performance.now() });
    
    if (filesList.length === 0) {
      // User cancelled - onChange fires with empty files array
      cleanupCancel();
      return;
    }
    
    // User selected files - mark as being processed
    filesBeingProcessedRef.current = true;
    logger.log('[handleFileChange] State after setting processed flag:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection
    });
    
    // Spinner STAYS UP — file processing/upload work starts now

    // Previously we cleared existing files on homepage (non-chat page) selection; removed to preserve prior uploads.
    // Old files now persist until user explicitly removes or clears them, or they are invalidated by tool change (e.g., data_analysis pruning).

  // Dynamic limits for general chat: up to 2 files normally, but if ALL selected + existing are images, allow up to 5.
    const fileType = (f: File) => f.type || 'application/octet-stream';
    const isExcelCsvFile = (f: File) =>
      fileType(f) === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType(f) === 'application/vnd.ms-excel' ||
      fileType(f) === 'text/csv' ||
      /\.csv$/i.test(f.name);
    // Check if ANY file in the selection is CSV/Excel (not just the first one),
    // so mixed drops containing a CSV/Excel still trigger data_analysis mode.
    const isExcelCsv = filesList.some(isExcelCsvFile);
    // Only auto-switch to data_analysis if not already in translation mode (CSV/Excel work in both modes)
    if(isExcelCsv && selectedTool !== 'translation') {
      dispatch(setSelectedTool('data_analysis'));
      setIsFileUploadActive(false); // Hide File Upload pill when auto-switching to Data Analysis
    }
    const isTranslationMode = selectedTool === 'translation';
    const isAnalysisMode = (selectedTool === 'data_analysis' || isExcelCsv) && !isTranslationMode;
    // Route warnings to the correct state: translation gets its own dialog, others share the general one
    const warnFn = isTranslationMode
      ? (msg: string) => setFileWarning({ title: 'File Not Accepted', message: msg, mode: 'translation' })
      : (msg: string) => setFileWarning({ message: msg, mode: 'general' });
    
    // Use staged files if dialog is open, otherwise use uploaded files
    // NOTE: use refs here — this callback has a fixed dep array and would otherwise
    // read stale values of isFileUploadDialogOpen / stagedFiles.
    const dialogOpen = isFileUploadDialogOpenRef.current;
    const existingFiles = dialogOpen ? stagedFilesRef.current : uploadedFiles;
    
    const existingCategories = new Set(existingFiles.map(f => {
      const name = f.name;
      const type = f.type;
      const mockFile = { name, type } as File;
      return getFileCategory(mockFile);
    }));
    
    const incomingCategories = new Set(filesList.map(getFileCategory));
    
    // When data_analysis mode is active (explicit or auto-detected from a CSV/Excel in a
    // mixed batch), pre-filter filesList to only CSV/Excel files BEFORE the slot-limit check.
    // Without this, computeFileLimits would take the first N files by position, potentially
    // picking non-CSV files and leaving the actual CSV behind.
    if (isAnalysisMode) {
      const csvExcelFiles = filesList.filter(isExcelCsvFile);
      const nonCsvExcel = filesList.filter(f => !isExcelCsvFile(f));
      if (nonCsvExcel.length > 0) {
        const names = nonCsvExcel.map(f => f.name).join(', ');
        setFileWarning(prev => ({
          title: 'File Not Accepted',
          message: (prev?.message ? prev.message + '\n\n' : '') +
            `${names}: Unsupported type (CSV/Excel only)`,
          mode: 'general'
        }));
      }
      filesList.splice(0, filesList.length, ...csvExcelFiles);
    }

    // Check for mixing across the three main categories (image, data, text)
    // Skip category checking for translation mode - it has its own file type restrictions
    if (!isAnalysisMode && !isTranslationMode && incomingCategories.size > 1) {
      // Mixed incoming files - need to pick one category
      const categoryPriority: ('image' | 'data' | 'text')[] = ['image', 'data', 'text'];
      const chosenCategory = categoryPriority.find(cat => incomingCategories.has(cat)) || 'image';
      
      const kept = filesList.filter(f => getFileCategory(f) === chosenCategory);
      const discarded = filesList.filter(f => getFileCategory(f) !== chosenCategory);
      
      const discardedCategoriesSet = new Set(discarded.map(getFileCategory));
      const discardedCategoryNames = Array.from(discardedCategoriesSet).filter(c => c !== 'unknown').join(' and ');
      
      warnFn(`Cannot mix file types. Keeping ${chosenCategory} file(s): ${kept.map(f=>f.name).join(', ')}. Discarded ${discardedCategoryNames} file(s): ${discarded.map(f=>f.name).join(', ')}`);
      filesList.splice(0, filesList.length, ...kept);
    } else if (!isAnalysisMode && !isTranslationMode && existingCategories.size > 0 && incomingCategories.size > 0) {
      // Check if incoming category conflicts with existing category
      const existingCategory = Array.from(existingCategories)[0]; // Should only be one category
      const incomingCategory = Array.from(incomingCategories)[0];
      
      if (existingCategory !== incomingCategory && existingCategory !== 'unknown' && incomingCategory !== 'unknown') {
        const discardedNames = filesList.map(f=>f.name).join(', ');
        warnFn(`Cannot mix ${incomingCategory} file(s) with existing ${existingCategory} file(s). Discarded: ${discardedNames}`);
        if (fileInputRef.current) fileInputRef.current.value='';
        cleanupCancel();
        return; // Discard all incoming files
      }
    }

    const allImagesCombined = (!isAnalysisMode && !isTranslationMode) && ((existingCategories.has('image') || existingFiles.length === 0) && filesList.length > 0 && filesList.every(isImageFile));
    const { maxTotal: MAX_TOTAL, hardLimitReached, effectiveFiles: limitedFiles, warning: limitWarning } = computeFileLimits(
      filesList,
      existingFiles.length,
      { isAnalysisMode, isTranslationMode, allImagesCombined }
    );
    let effectiveFiles: File[] = limitedFiles;
    // Track files discarded purely by the count limit (not type/size) so we can surface
    // the "Discarded" info AFTER type validation — only mentioning files that were truly
    // cut by the slot cap, not ones that would have been rejected anyway.
    const discardedByLimitNames: string[] = limitWarning && !hardLimitReached
      ? filesList.slice(effectiveFiles.length).map(f => f.name)
      : [];
    // For hard-limit (no slots left at all), show the warning immediately and bail out.
    // For partial truncation we defer until Promise.all so we can merge with type errors.
    if (hardLimitReached && limitWarning) setFileWarning(limitWarning);
    if (hardLimitReached) {
      if (isTranslationMode) {
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
      } else {
        cleanupCancel();
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Allowed types differ by mode
    const analysisMime = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const translationMime = [
      'text/plain',
      'text/html',
      'text/csv',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-outlook'
    ];
    const generalMime = [
      'image/png', 'image/jpeg',
      'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024; // 5MB per image file
    const MAX_OTHER_BYTES = 16 * 1024 * 1024; // 16MB for non-image files

    // Collect all translation-mode file errors so we can show ONE combined dialog
    // instead of overwriting the warning for each invalid file independently.
    const translationFileErrors: Array<{ fileName: string; hasTypeError: boolean; hasSizeError: boolean }> = [];

    const readPromises: Promise<{ name: string; type: string; data: string } | null>[] = effectiveFiles.map(file => {
      return new Promise(resolve => {
        // Basic validations per file - check both type and size, report all errors
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel';
        const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
        const isImage = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const isPpt = /\.pptx?$/i.test(file.name) || file.type === 'application/vnd.ms-powerpoint' || file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        const isPptx = /\.pptx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        const isDoc = /\.docx?$/i.test(file.name) || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const isDocx = /\.docx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const isXlsx = /\.xlsx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
        const isHtml = /\.html?$/i.test(file.name) || file.type === 'text/html';
        const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
        const isMsg = /\.msg$/i.test(file.name) || file.type === 'application/vnd.ms-outlook';

        const allowedSet = isAnalysisMode ? analysisMime : isTranslationMode ? translationMime : generalMime;
        const mimeAllowed = allowedSet.includes(file.type);
        const extAllowedGeneral = isImage || isCsv || isXlsx || isPptx || isDocx || isTxt || isPdf;
        const extAllowedAnalysis = isCsv || isExcel;
        const extAllowedTranslation = isTxt || isHtml || isCsv || isDocx || isPptx || isXlsx || isMsg;
        const extAllowed = isAnalysisMode ? extAllowedAnalysis : isTranslationMode ? extAllowedTranslation : extAllowedGeneral;
        const isTypeValid = mimeAllowed || extAllowed;
        
        const isImageStrict = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const TRANSLATION_MAX_BYTES = 10 * 1024 * 1024; // 10MB limit for translation files
        const sizeLimit = isImageStrict ? MAX_IMAGE_BYTES : isTranslationMode ? TRANSLATION_MAX_BYTES : MAX_OTHER_BYTES;
        const isSizeValid = file.size <= sizeLimit;
        
        // Collect all errors for this file
        const errors: string[] = [];
        
        if (!isTypeValid) {
          errors.push(isAnalysisMode ? 'Unsupported type (CSV/Excel only)' : 'Unsupported file type. Legacy formats (.doc, .ppt, .xls) are not supported — please use .docx, .pptx, or .xlsx');
        }
        
        if (!isSizeValid) {
          const humanLimit = isImageStrict ? MAX_MB + 'MB image limit' : isTranslationMode ? '10MB limit' : '16MB limit';
          errors.push(isTranslationMode ? `exceeds the 10MB file size limit` : `Exceeds ${humanLimit}`);
        }
        
        // If any errors, report them all and skip this file
        if (errors.length > 0) {
          console.warn('[upload] rejected_file', { name: file.name, size: file.size, type: file.type, errors });
          if (isTranslationMode) {
            // Defer the warning — collect all invalid files first so a single combined
            // dialog is shown after Promise.all resolves (avoids overwriting per-file).
            translationFileErrors.push({ fileName: file.name, hasTypeError: !isTypeValid, hasSizeError: !isSizeValid });
            return resolve(null);
          }
          const hasTypeError = !isTypeValid;
          const hasSizeError = !isSizeValid;
          const errorMessage = `${file.name}: ${errors.join(' and ')}`;
          setFileWarning(prev => {
            const msg = prev ? prev.message + `\n\n${errorMessage}` : errorMessage;
            const title = prev?.title ?? (hasSizeError && !hasTypeError ? (isImageStrict ? 'Image Too Large' : 'File Too Large') : undefined);
            return { title, message: msg, mode: 'general' };
          });
          return resolve(null);
        }
        const isDuplicate = existingFiles.some(f => f.name === file.name && f.lastModified === file.lastModified);
        if (isDuplicate) {
          console.warn('Duplicate file skipped.', file.name);
          if (isTranslationMode) {
            setFileWarning({ title: 'File Not Accepted', message: `${file.name} has already been added.`, mode: 'translation' });
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
          } else {
            const dupMessage = `Duplicate file skipped: ${file.name}`;
            setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | ${dupMessage}` } : { message: dupMessage, mode: 'general' });
          }
          return resolve(null);
        }
        const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
        if (dialogOpen) {
          setStagedFiles(prev => [...prev, {
            name: file.name,
            type: file.type,
            data: '',
            size: file.size,
            lastModified: file.lastModified,
            status: 'uploading',
            key: uniqueKey,
          }]);
        }
        const reader = new FileReader();
        if (isTranslationMode) {
          translationActiveReadersRef.current.push(reader);
        } else if (dialogOpen) {
          dialogActiveReadersRef.current.push(reader);
        } else if (!isAnalysisMode) {
          generalActiveReadersRef.current.push(reader);
        }
        
        logger.log('[FileReader] About to start reading file', { fileName: file.name, fileType: file.type, timestamp: performance.now() });
        
        reader.onload = () => {
          logger.log('[FileReader] File read complete', { fileName: file.name, timestamp: performance.now() });
          
          // Capture raw result but defer all processing into prepareMeta.
          // arrayBufferToBase64 blocks the event loop; yielding first lets any queued
          // cancel click run before we make the dispatch decision.
          const rawResult = reader.result;
          const prepareMeta = async () => {
            // Yield via a macrotask before any heavy work. Using setTimeout(0) posts
            // the continuation to the task queue so the browser can process pending
            // input events (including a queued cancel mousedown) first, making it
            // much more likely that the latest cancel flag is visible here.
            await new Promise<void>(r => setTimeout(r, 0));

            // User cancelled while file was being read — do not add the file
            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            // Decode the raw result to a base64 string NOW (safely after the cancel check).
            // arrayBufferToBase64 is a synchronous byte-by-byte loop that blocks the JS event
            // loop for several seconds on large binary files and slow CPUs. Any cancel click the
            // user makes during that block is queued by the OS and cannot run until the loop ends.
            let dataString = '';
            if (typeof rawResult === 'string') {
              // For images readAsDataURL would give full data URL; we prefer raw base64 payload like existing logic.
              // If result starts with data: prefix, strip metadata to keep consistency.
              if (rawResult.startsWith('data:')) {
                const commaIdx = rawResult.indexOf(',');
                dataString = commaIdx >= 0 ? rawResult.substring(commaIdx + 1) : rawResult; // strip header
              } else {
                dataString = rawResult;
              }
            } else if (rawResult instanceof ArrayBuffer) {
              dataString = arrayBufferToBase64(rawResult);
            }

            // Second yield via a macrotask: arrayBufferToBase64 may have just blocked the event
            // loop for several seconds. Any cancel click queued during that block is now
            // waiting in the input event queue. Scheduling a macrotask via setTimeout lets
            // those input events run before we reach this check, without tying progress to rAF.
            await new Promise<void>(r => setTimeout(r, 0));

            // Check again after the potentially-blocking base64 conversion.
            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            let metaFile = { name: file.name, type: file.type, data: dataString };
            
            // Convert PPTX, DOC, and TXT files to PDF using FastAPI endpoint
            // Skip conversion for analysis mode and translation mode (they need original files)
            if (!isAnalysisMode && !isTranslationMode && (isPpt || isDoc || isTxt) && !isMsg) {
              try {
                // Get FastAPI endpoint from env or use default
                const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
                const conversionEndpoint = process.env.REACT_APP_FILE_CONVERSION_URL 
                  || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

                metaFile = await convertFileToPdf(file, conversionEndpoint, msalInstance);
                // convertFileToPdf never throws — on failure it silently returns the
                // original file with original MIME type. Log a warning so we know.
                if (metaFile.type !== 'application/pdf') {
                  console.warn('[upload] Conversion returned non-PDF result (server conversion likely failed), using original file:', metaFile.type);
                }
              } catch (error) {
                console.error('[upload] Conversion failed, using original file:', error);
              }
            }

            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            const metaRedux = {
              name: metaFile.name,
              size: file.size,
              type: metaFile.type,
              lastModified: file.lastModified,
              data: metaFile.data,
              key: uniqueKey,
              status: 'pending' as const
            };

            // If dialog is open, add to staged files (don't add to Redux yet)
            if (dialogOpen) {
              setStagedFiles(prev => prev.map(stagedFile => (
                stagedFile.key === uniqueKey
                  ? { ...metaRedux, key: uniqueKey }
                  : stagedFile
              )));
            } else {
              // Normal flow - add to Redux immediately
              if (isDirectDropBatchActiveRef.current) {
                directDropBatchFileNamesRef.current.push(metaRedux.name);
              }
              dispatch(addUploadedFile(metaRedux));
            }
            
            resolve(metaFile);
          };
          prepareMeta();
        };
        // Binary types (excel, image, ppt, doc, pdf, msg) read as ArrayBuffer for base64; csv, xml, txt as text
        if (isExcel || isImage || isPpt || isDoc || isPdf || isMsg) {
          logger.log('[FileReader] Starting readAsArrayBuffer (BLOCKING)', { fileName: file.name, timestamp: performance.now() });
          reader.readAsArrayBuffer(file);
        } else {
          logger.log('[FileReader] Starting readAsText', { fileName: file.name, timestamp: performance.now() });
          reader.readAsText(file);
        }
      });
    });
    Promise.all(readPromises).then(results => {
      if (isTranslationMode) {
        translationActiveReadersRef.current = [];
      } else if (dialogOpen) {
        dialogActiveReadersRef.current = [];
      } else if (!isAnalysisMode) {
        generalActiveReadersRef.current = [];
      }

      // Show a single combined warning for all translation files that failed validation.
      if (isTranslationMode && translationFileErrors.length > 0) {
        let errorMessage: string;
        if (translationFileErrors.length === 1) {
          const e = translationFileErrors[0];
          if (e.hasTypeError && e.hasSizeError) {
            errorMessage = `${e.fileName} has an unsupported format and exceeds the 10MB size limit.\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg \nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
          } else if (e.hasTypeError) {
            errorMessage = `${e.fileName} has an unsupported file format.\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg \nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
          } else {
            errorMessage = `${e.fileName} exceeds the 10MB file size limit. Please upload a smaller file.`;
          }
        } else {
          const errorLines = translationFileErrors.map(e => {
            if (e.hasTypeError && e.hasSizeError) return `• ${e.fileName} — unsupported format and exceeds 10MB`;
            if (e.hasTypeError) return `• ${e.fileName} — unsupported file format`;
            return `• ${e.fileName} — exceeds the 10MB file size limit`;
          }).join('\n');
          errorMessage = `The following files could not be added:\n\n${errorLines}\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg\nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
        }
        setFileWarning({ title: 'File Not Accepted', subtitle: translationFileErrors.length > 1 ? null : undefined, message: errorMessage, mode: 'translation' });
        // If every file was rejected, clear the awaiting states immediately.
        const validResults = results.filter(Boolean);
        if (validResults.length === 0) {
          setAwaitingFileSelection(false);
          filesBeingProcessedRef.current = false;
          dispatch(hideGlobalBlocking());
          return;
        }
      }

      const metas = results.filter(Boolean) as { name: string; type: string; data: string }[];

      // Now that we know which files actually passed validation, surface the deferred
      // "Discarded by limit" info. Only show it when at least one file was valid;
      // if everything was rejected by type/size the limit truncation is irrelevant.
      if (discardedByLimitNames.length > 0 && metas.length > 0) {
        const discardedList = discardedByLimitNames.map(n => `- ${n}`).join('\n');
        const addedList = metas.map(m => `- ${m.name}`).join('\n');
        setFileWarning(prev => {
          const base = prev
            ? prev
            : { title: 'Maximum Files Reached', message: '', mode: 'general' as const };
          const limitNote = `Only ${MAX_TOTAL} files are allowed.\n\nAdded:\n${addedList}\n\nDiscarded:\n${discardedList}`;
          return { ...base, title: 'Maximum Files Reached', message: base.message ? `${base.message}\n\n${limitNote}` : limitNote };
        });
      }

      if (metas.length > 0) {
        // Check if files are CSV/Excel (data analysis files) - this handles both explicit data_analysis mode
        // and auto-detection from BYOD upload. Use the computed isAnalysisMode from earlier in the function.
        if (isAnalysisMode) {
          // If dialog is open, files are staged and will upload when user clicks "Upload" button
          // If dialog is NOT open (drag-and-drop or direct file picker), upload immediately
          if (dialogOpen) {
            logger.log('[handleFileChange] Data analysis mode - files staged for dialog confirmation');
            requestAnimationFrame(() => {
              setAwaitingFileSelection(false);
              filesBeingProcessedRef.current = false;
              dispatch(hideGlobalBlocking());
            });
          } else {
            logger.log('[handleFileChange] Data analysis mode - uploading files immediately (drag-and-drop or direct picker)');
            uploadFilesBatch(metas);
            // awaitingFileSelection will be cleared by uploadFilesBatch when complete
          }
        } else {
          // General chat: files staged locally with 'pending' status, no backend upload needed
          logger.log('[handleFileChange] General chat mode - staging locally:', metas.map(m => m.name));
          logger.log('[handleFileChange] Clearing awaiting states - files now in Redux');
          // Clear all awaiting states - files are now in Redux
          // Extended delay to ensure files are fully processed and visible before clearing waiting indicator
          setTimeout(() => {
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
            dispatch(hideGlobalBlocking());
          }, 1000);
        }
      } else {
        // No valid files after filtering (all invalid); clear waiting indicator.
        logger.log('[handleFileChange] No valid files - clearing all states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTool, uploadedFiles]);

  // Paste handler: treat pasted images like a lightweight general chat upload (no spinner / picker overlay).
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
  try {
    if (isStreaming) return;

    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    const itemsArray = Array.from(items);

    const imageItems = itemsArray.filter(item => item.type?.startsWith("image/"));
    const textItems = itemsArray.filter(
      item => item.type === "text/plain" || item.type === "text/rtf"
    );

    // Only process images if there is at least one image AND no real text
    if (imageItems.length === 0 || textItems.length > 0) return;

    // Block image pastes when file upload is disabled (RAG modes, 3M Knowledge)
    if (shouldHideTools) {
      e.preventDefault();
      return;
    }

    // Image generation mode blocks image pastes
    if (effectiveToolForSend === 'image_generation') {
      setFileWarning(prev =>
        prev
          ? { ...prev, message: prev.message + ' | Image Generation does not support file uploads, including images.' }
          : { message: 'Image Generation tool does not support pasted images.', mode: 'general' }
      );
      e.preventDefault();
      return;
    }

    // Data analysis mode blocks image pastes
    if (selectedTool === 'data_analysis') {
      setFileWarning(prev =>
        prev
          ? { ...prev, message: prev.message + ' | Data Analysis only supports CSV or Excel files.' }
          : { message: 'Data Analysis tool only supports CSV or Excel files. Pasted images are not allowed.', mode: 'general' }
      );
      e.preventDefault();
      return;
    }

    e.preventDefault(); // prevent default image paste

    // Convert image items to File objects
    const imageFiles: File[] = imageItems.map(item => {
      const blob = item.getAsFile();
      if (!blob) return null;

      const ext =
        item.type === 'image/png'
          ? 'png'
          : item.type === 'image/jpeg'
          ? 'jpg'
          : 'img';

      return new File(
        [blob],
        `pasted_image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`,
        { type: item.type }
      );
    }).filter(Boolean) as File[];

    // Show confirmation dialog if needed
    if (hasMessages && selectedTool) {
      setConfirmationConfig({
        title: 'Paste Image?',
        message:
          'Pasting an image will switch to general chat mode and clear your chat history. Are you sure you want to continue?',
        onConfirm: () => {
          if (onClearChat) onClearChat();
          dispatch(setSelectedTool(null));
          dispatch(clearTargetLanguages());
          manuallyClearedToolRef.current = true;

          setShowConfirmation(false);
          setConfirmationConfig(null);

          processPastedImagesFromFiles(imageFiles);
        }
      });

      setShowConfirmation(true);
      return;
    }

    processPastedImagesFromFiles(imageFiles);

  } catch (err) {
    console.warn('[paste-images] failed', err);
  }
};



  // Helper function to process pasted images from File objects
  const processPastedImagesFromFiles = (imageFiles: File[]) => {
    try {
      if (imageFiles.length === 0) return;
      
      // Validate and process each file
      const validFiles: File[] = [];
      
      for (const file of imageFiles) {
        // Disallow mixing with existing non-image files (mirror selection rules)
        const existingNonImages = uploadedFiles.some(f => !(/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)));
        if (existingNonImages) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ' | Cannot paste image while non-image files attached.' } : { message: 'Cannot paste image while non-image files are already attached.', mode: 'general' });
          return; // abort entire paste batch
        }
        
        // Enforce remaining image slots (5 for pure images context)
        const existingImagesCount = uploadedFiles.filter(f => (/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name))).length;
        const remainingSlots = 5 - existingImagesCount;
        
        if (validFiles.length >= remainingSlots) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ' | Image limit reached (5).' } : { message: 'Image limit reached (5). Remove one to paste another.', mode: 'general' });
          break; // Stop processing more files
        }
        
        // Size gate (5MB per image)
        const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024;
        if (file.size > MAX_IMAGE_BYTES) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | Pasted image too large (>` + MAX_MB + `MB): ${file.name}` } : { message: `Pasted image exceeds ` + MAX_MB + `MB and was skipped: ${file.name}`, mode: 'general' });
          continue;
        }
        
        // Duplicate check (by name)
        const isDuplicate = uploadedFiles.some(f => f.name === file.name);
        if (isDuplicate) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | Duplicate pasted image skipped: ${file.name}` } : { message: `Duplicate pasted image skipped: ${file.name}`, mode: 'general' });
          continue;
        }
        
        validFiles.push(file);
      }
      
      // If no valid files after filtering, return
      if (validFiles.length === 0) return;
      
      // Read & dispatch similarly to general chat uploads (status 'pending', no immediate upload unless future tool selection requires)
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          let dataString = '';
          if (typeof result === 'string') {
            // For images readAsDataURL would give full data URL; we prefer raw base64 payload like existing logic.
            // If result starts with data: prefix, strip metadata to keep consistency.
            if (result.startsWith('data:')) {
              const commaIdx = result.indexOf(',');
              dataString = commaIdx >= 0 ? result.substring(commaIdx + 1) : result; // strip header
            } else {
              dataString = result;
            }
          } else if (result instanceof ArrayBuffer) {
            dataString = arrayBufferToBase64(result);
          }
          const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
          dispatch(addUploadedFile({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            data: dataString,
            key: uniqueKey,
            status: 'pending'
          }));
        };
        // Use ArrayBuffer to mirror original image processing path
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      console.warn('[paste-images-processing] failed', err);
    }
  };

  const handleRemoveFile = (name: string) => {
    const doRemove = () => {
      const willBeEmpty = uploadedFiles.length === 1;
      dispatch(removeUploadedFile(name));
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Deselect the data_analysis tool when file is removed
      if (selectedTool === 'data_analysis') {
        dispatch(setSelectedTool(null));
        manuallyClearedToolRef.current = true;
        // Clear history and reset to home state when file is removed
        if (onClearChat) {
          onClearChat();
        }
      }

      // Reset Compare active state when files are removed
      setIsCompareActive(false);

      // Hide File Upload pill if this was the last file
      if (willBeEmpty) {
        setIsFileUploadActive(false);
      }

      setShowConfirmation(false);
      setConfirmationConfig(null);
    };

    // No messages — remove silently for all cases.
    if (!hasMessages) {
      doRemove();
      return;
    }

    // data_analysis with messages: removing disables the tool and clears chat history.
    if (selectedTool === 'data_analysis') {
      setConfirmationConfig({
        title: 'Remove File?',
        message: 'Removing this file will disable the Data Analysis tool and clear your chat history. Are you sure you want to continue?',
        onConfirm: doRemove,
      });
      setShowConfirmation(true);
      return;
    }

    // Other file types with an existing conversation: chat loses access to file content.
    setConfirmationConfig({
      title: 'Remove File?',
      message: 'Removing this file means the chat will no longer have access to its content. Are you sure you want to continue?',
      onConfirm: doRemove,
    });
    setShowConfirmation(true);
  };

  const handleClearAllFiles = () => {
    const doClear = (clearChat: boolean) => {
      if (clearChat && onClearChat) {
        onClearChat();
      }

      // Clear files, input, and target languages
      setValue('');
      dispatch(clearUploadedFiles());
      dispatch(clearTargetLanguages());
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Hide File Upload pill when all files are cleared
      setIsFileUploadActive(false);

      // Clear awaiting states and spinner when clearing all files
      setAwaitingFileSelection(false);
      filesBeingProcessedRef.current = false;
      dispatch(hideGlobalBlocking());

      // Reset Compare active state when all files are cleared
      setIsCompareActive(false);

      // If in data analysis mode, disable the tool since it requires files
      if (selectedTool === 'data_analysis') {
        dispatch(setSelectedTool(null));
        manuallyClearedToolRef.current = true;
        dispatch(clearTranslationData());
        setIsTranslationCollapsed(false);
      }

      // Clear selected languages when clearing files in translation mode
      if (selectedTool === 'translation') {
        dispatch(clearTargetLanguages());
      }

      setShowConfirmation(false);
      setConfirmationConfig(null);
    };

    // No messages — clear silently, no dialog needed
    if (!hasMessages) {
      doClear(false);
      return;
    }

    // data_analysis with messages: clearing disables the tool and clears chat history
    if (selectedTool === 'data_analysis') {
      setConfirmationConfig({
        title: 'Clear All Files?',
        message: 'Clearing all files will disable the Data Analysis tool and clear your chat history. Are you sure you want to continue?',
        onConfirm: () => doClear(true),
      });
      setShowConfirmation(true);
      return;
    }

    // Other cases with messages: chat loses access to file content, but history is preserved
    setConfirmationConfig({
      title: 'Clear All Files?',
      message: 'Clearing all files means the chat will no longer have access to their content. Are you sure you want to continue?',
      onConfirm: () => doClear(false),
    });
    setShowConfirmation(true);
  };

  const handleFileUploadDialogDropzoneClick = () => {
    // When user clicks the dropzone in the dialog, trigger the actual file picker
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setHideFileUploadDialogDropzone(false);
      
      logger.log('[handleFileUploadDialogDropzoneClick] Setting awaiting states and showing spinner');
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = false;
      
      fileInputRef.current.click();
    }
  };

  const handleFileUploadDialogUpload = () => {
    // When user clicks "Upload" button in dialog - confirm the staged files
    logger.log('[handleFileUploadDialogUpload] Confirming upload of staged files:', stagedFiles.map(f => f.name));
    const readyFiles = stagedFiles.filter(file => file.status !== 'uploading' && !!file.data);
    if (readyFiles.length === 0) {
      return;
    }
    
    // Add all staged files to Redux
    readyFiles.forEach(file => dispatch(addUploadedFile(file)));
    
    // Check if files need backend upload (data analysis mode)
    const isAnalysisMode = selectedTool === 'data_analysis';
    if (isAnalysisMode && readyFiles.length > 0) {
      // Show spinner IMMEDIATELY before the async upload starts
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = true;
      // Upload to backend
      const metas = readyFiles.map(f => ({ name: f.name, type: f.type, data: f.data }));
      uploadFilesBatch(metas);
    }
    
    // Clear staged files and close dialog
    setStagedFiles([]);
    setHideFileUploadDialogDropzone(false);
    setIsFileUploadDialogOpen(false);
  };

  const handleFileUploadDialogClose = () => {
    // When user clicks "Cancel" button in dialog or clicks outside
    // Discard all staged files without adding them to Redux
    logger.log('[handleFileUploadDialogClose] Canceling and discarding staged files:', stagedFiles.map(f => f.name));
    
    // Clear staged files
    setStagedFiles([]);
    
    // Clear awaiting states
    setAwaitingFileSelection(false);
    filesBeingProcessedRef.current = false;
    dispatch(hideGlobalBlocking());
    
    // Disable tool when user cancels the FileUploadDialog
    // Mark as manually cleared to prevent auto-restore from initialTools
    if (selectedTool === 'data_analysis' || selectedTool === 'translation') {
      manuallyClearedToolRef.current = true;
      dispatch(setSelectedTool(null));
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
    }
    
    // Clear file upload active state if no files remain after cancel
    if (uploadedFiles.length === 0) {
      setIsFileUploadActive(false);
    }
    
    setHideFileUploadDialogDropzone(false);
    setIsFileUploadDialogOpen(false);
  };

  const handleFileUploadDialogCancelFileRead = () => {
    // Abort any in-progress FileReader instances so large files stop reading immediately
    abortAndClearReaders(dialogActiveReadersRef);
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setStagedFiles([]);
    setHideFileUploadDialogDropzone(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(hideGlobalBlocking());
  };

  // Called when files are dropped onto the FileUploadDialog dropzone.
  // Builds a synthetic ChangeEvent and routes through handleFileChange so all
  // existing validation (type, size, duplicates, limits) runs identically to
  // a normal file-picker selection.
  const handleFilesDroppedOnDialog = React.useCallback((files: File[]) => {
    if (!files.length) return;
    const dt = new DataTransfer();
    files.forEach(f => dt.items.add(f));
    // Set files on the hidden input so handleFileChange reads e.target.files correctly
    if (fileInputRef.current) {
      fileInputRef.current.files = dt.files;
    }
    const syntheticEvent = {
      target: { files: dt.files },
      nativeEvent: new Event('change'),
      currentTarget: fileInputRef.current,
      bubbles: true,
      cancelable: false,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: false,
      preventDefault: () => {},
      isDefaultPrevented: () => false,
      stopPropagation: () => {},
      isPropagationStopped: () => false,
      persist: () => {},
      timeStamp: Date.now(),
      type: 'change',
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(syntheticEvent);
  }, [handleFileChange]);

  const handleCancelDirectDropUpload = React.useCallback(() => {
    abortAndClearReaders(generalActiveReadersRef);
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setShowImmediateSpinner(false);
    setIsDraggingOver(false);
    dispatch(hideGlobalBlocking());

    Array.from(new Set(directDropBatchFileNamesRef.current)).forEach(name => {
      dispatch(removeUploadedFile(name));
    });

    directDropBatchFileNamesRef.current = [];
    isDirectDropBatchActiveRef.current = false;

    // Clear any tool that was auto-selected during the drop (e.g. data_analysis for CSV/Excel files)
    manuallyClearedToolRef.current = true;
    dispatch(setSelectedTool(null));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [dispatch]);

  const handleRemoveStagedFile = (fileName: string) => {
    // Remove a file from the staged files list
    logger.log('[handleRemoveStagedFile] Removing staged file:', fileName);
    setStagedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  // Drag and drop handlers for ChatInput
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't allow drag and drop during streaming, global blocking (spinner), or for certain tools
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    // When the file-upload dialog is open, let it handle its own drag-and-drop — don't
    // show the ChatInput overlay behind it.
    if (isFileUploadDialogOpen) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    // Don't show the drag overlay while files are actively being uploaded/processed
    if (awaitingFileSelection || filesBeingProcessedRef.current) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    e.dataTransfer.dropEffect = 'copy';
    
    // In translation mode, suppress the full-width ChatInput overlay — only the upload
    // column in TranslationMode should highlight (via its own isDragOverUpload state).
    if (selectedTool === 'translation') return;
    
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only clear dragging state if we're actually leaving the drop zone
    // relatedTarget is null when leaving the element entirely
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingOver(false);
      setShowImmediateSpinner(false); // Clear spinner if they cancel
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If drag-and-drop is not allowed, signal to browser that drop is not permitted
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    // When the file-upload dialog is open, suppress the ChatInput overlay.
    if (isFileUploadDialogOpen) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    // Don't re-show overlay while files are actively being uploaded/processed
    if (awaitingFileSelection || filesBeingProcessedRef.current) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    // Allow drops
    e.dataTransfer.dropEffect = 'copy';
    
    // In translation mode, keep isDraggingOver false — only the upload column highlights.
    if (selectedTool === 'translation') return;
    
    // Ensure overlay stays visible while dragging within the zone
    if (!isDraggingOver) setIsDraggingOver(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear file drag state immediately - user has dropped the file
    setIsFileDragInProgress(false);

    // Don't allow drag and drop during streaming, global blocking (spinner), or for certain tools
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      setIsDraggingOver(false);
      e.dataTransfer.dropEffect = 'none';
      // Show custom blocking message instead of browser security message
      setShowBlockingMessage(true);
      setTimeout(() => setShowBlockingMessage(false), 3000); // Auto-hide after 3 seconds
      return;
    }

    // When the file-upload dialog is open, ignore drops on the ChatInput backdrop.
    if (isFileUploadDialogOpen) {
      setIsDraggingOver(false);
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      logger.log('[handleDrop] Drop event triggered', { fileCount: files.length, timestamp: performance.now() });

      // Translation mode: route through translationFileInputRef so handleFileChange
      // applies the correct translation-specific validation (type, size, count).
      if (selectedTool === 'translation') {
        setIsDraggingOver(false);
        if (translationFileInputRef.current) {
          setAwaitingFileSelection(true);
          filesBeingProcessedRef.current = true;
          const dataTransfer = new DataTransfer();
          Array.from(files).forEach(file => dataTransfer.items.add(file));
          translationFileInputRef.current.files = dataTransfer.files;
          const event = new Event('change', { bubbles: true });
          translationFileInputRef.current.dispatchEvent(event);
        }
        return;
      }
      
      // Early file limit validation - check before processing
      const currentFileCount = uploadedFiles.length;
      const isAnalysisMode = selectedTool === 'data_analysis';
      const isDirectGeneralChatDrop = !selectedTool;

      // Use shared computeFileLimits helper (same logic as handleFileChange)
      const droppedFilesArray = Array.from(files);
      const existingAreAllImages = uploadedFiles.length === 0 || uploadedFiles.every(f => /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name));
      const droppedAreAllImages = droppedFilesArray.every(f => /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name));
      const allImagesCombined = !isAnalysisMode && existingAreAllImages && droppedAreAllImages && droppedFilesArray.length > 0;
      const { hardLimitReached: dropHardLimit, warning: dropLimitWarning } = computeFileLimits(
        droppedFilesArray,
        currentFileCount,
        { isAnalysisMode, isTranslationMode: false, allImagesCombined }
      );
      // For a hard limit (no slots at all) bail out immediately with a warning.
      // For partial truncation do NOT set a warning here — pass all files to handleFileChange
      // so it can run type validation first and then build the correct "Added/Discarded"
      // message (same as the file-picker path does via discardedByLimitNames).
      if (dropHardLimit) {
        if (dropLimitWarning) setFileWarning(dropLimitWarning);
        setIsDraggingOver(false);
        return;
      }
      
      logger.log('[handleDrop] Converting drag overlay to spinner immediately', { timestamp: performance.now() });

      if (isDirectGeneralChatDrop) {
        directDropBatchFileNamesRef.current = [];
        isDirectDropBatchActiveRef.current = true;
        setStagedFiles([]);
        setHideFileUploadDialogDropzone(true);
        isFileUploadDialogOpenRef.current = true;
        setIsFileUploadDialogOpen(true);
        setShowImmediateSpinner(false);
        setIsDraggingOver(false);
      } else {
        // For all other modes (data_analysis etc.) clear the drag overlay immediately so
        // the "Drop files here" message doesn't linger while files are uploading.
        setIsDraggingOver(false);
      }
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = true;
      
      logger.log('[handleDrop] Starting file processing immediately', { timestamp: performance.now() });
      
      // Pass all dropped files to handleFileChange — it applies the same computeFileLimits
      // logic internally and builds the correct "Added/Discarded" warning after type validation.
      const dataTransfer = new DataTransfer();
      droppedFilesArray.forEach(file => dataTransfer.items.add(file));
      
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        const event = new Event('change', { bubbles: true });
        fileInputRef.current.dispatchEvent(event);
      }
      
      if (!isDirectGeneralChatDrop) {
        // Non-general-chat direct drops keep using the blocking loader.
        dispatch(showGlobalBlocking('Processing files...'));
      }
      
      logger.log('[handleDrop] File input event dispatched', { timestamp: performance.now() });
      
      if (!isDirectGeneralChatDrop) {
        setTimeout(() => {
          setShowImmediateSpinner(false);
          // isDraggingOver already cleared above; this is a safety net.
          setIsDraggingOver(false);
        }, 150);
      }
    } else {
      setIsDraggingOver(false);
    }
  };

  // Unified overlay logic: applies to all file uploads (data_analysis and general chat)
  useEffect(() => {
    const hasFiles = uploadedFiles.length > 0;
    const isAnalysis = selectedTool === 'data_analysis';
    
    // Use ref to check if files are being processed (handles async Redux update delay)
    const filesIncoming = filesBeingProcessedRef.current;
        
    if (!hasFiles && !awaitingFileSelection && !filesIncoming) {
      dispatch(hideGlobalBlocking());
      return;
    }
    if (uploadError) return; // error modal shows instead.
    
    const anyError = uploadedFiles.some(f => f.status === 'error');
    const allUploaded = uploadedFiles.length > 0 && uploadedFiles.every(f => f.status === 'uploaded');
    
    // For data analysis: spinner stays active from picker open through entire upload lifecycle
    // Only clear awaitingFileSelection when upload is complete or error occurs
    if (isAnalysis) {
      if (allUploaded || anyError) {
        // Only clear awaitingFileSelection when completely done or error
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    } else {
      // General chat: clear awaitingFileSelection as soon as files are in Redux
      if (awaitingFileSelection && (hasFiles || filesIncoming)) {
        // Files are in Redux or incoming - clear awaiting flag
        if (hasFiles && !isDirectDropBatchActiveRef.current) {
          setAwaitingFileSelection(false);
          // Do NOT set filesBeingProcessedRef.current = false here.
          // For multi-file drops, File 1 hitting Redux triggers this effect, but File 2's
          // prepareMeta is still pending its setTimeout(r,0) yield. Clearing the ref here
          // causes prepareMeta to abort File 2 before it can dispatch. The Promise.all.then
          // 1000ms timeout in handleFileChange handles the cleanup after ALL files are processed.
        }
      }
    }
  }, [selectedTool, uploadedFiles, dispatch, uploadError, awaitingFileSelection]);
  
  // Get active function call from last assistant message (for voice mode spinner)
  const activeFunctionCall = React.useMemo(() => {
    const messages = chatModeState.mode === 'Ask Chat' 
      ? chatModeState.askChatHistory 
      : chatModeState.ragHistory;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && lastMessage.functionCall?.isActive) {
      return lastMessage.functionCall;
    }
    return null;
  }, [chatModeState.askChatHistory, chatModeState.mode, chatModeState.ragHistory]);

  return (
    <>
      {/* Voice Mode Circle - shown immediately when voice mode is active AND on main content page */}
      {isVoiceModeActive && chatPage && createPortal(
        <VoiceCircle
          isListening={isVoiceModeListening}
          isPaused={isVoiceModePaused}
          audioLevel={audioLevel}
          frequencyData={frequencyData}
          isRestarting={isVoiceModeRestarting}
          showInactivityWarning={showInactivityWarning}
          hasFunctionCallInProgress={activeFunctionCall !== null}
          onPauseToggle={handleVoiceModePauseToggle}
          onStop={handleVoiceModeStop}
          isDarkMode={mode === 'dark'}
        />,
        document.body
      )}
      
      {/* Chat Input - hidden when voice mode is active on main content page, instant switch */}
      {!(isVoiceModeActive && chatPage) && (
        <motion.form 
          // No padding on ChatInput itself - parent containers handle alignment
          className={`w-full mx-auto flex flex-col justify-center ${!chatPage ? 'sm:pt-0' : ''}`}
          style={{ maxWidth: '2000px' }}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
      {/* Drag and drop overlay - wraps only the input container */}
      <div 
        className={`relative ${isDraggingOver ? 'dragging-active' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDraggingOver && (
          <div 
            className={`absolute inset-0 z-[9999] flex items-center justify-center ${showImmediateSpinner ? 'pointer-events-auto' : 'pointer-events-none'}`}
            onMouseDown={showImmediateSpinner ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCancelDirectDropUpload();
            } : undefined}
            style={{
              backgroundColor: 'rgba(94, 149, 237, 0.1)',
              border: '3px dashed #5E95ED',
              borderRadius: '8px'
            }}
          >
            {showImmediateSpinner ? (
              <div className="flex flex-col items-center gap-2 p-4 pt-2 pb-2 rounded-lg bg-[var(--color-secondary)] shadow-xl max-w-sm">
                <div className="flex items-center gap-2">
                  <div 
                    style={{
                      width: 42,
                      height: 42,
                      border: '5px solid rgba(94, 149, 237, 0.25)',
                      borderTop: '5px solid #5E95ED',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <p className="text-base font-semibold text-[#5E95ED]">Waiting for file<span className="animate-pulse">...</span></p>
                </div>
                <p className="text-xs text-[var(--color-text)] opacity-70 text-center">Click to cancel</p>
              </div>
            ) : (
              // Show drop message when hovering
              <div className="flex flex-col items-center gap-2 p-4 pt-2 pb-2 rounded-lg bg-[var(--color-secondary)] shadow-xl max-w-sm" style={{ pointerEvents: 'none' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="text-base font-semibold text-[#5E95ED]">Drop files here to upload</p>
                <p className="text-xs text-[var(--color-text)] opacity-70 text-center">
                  {selectedTool === 'data_analysis' 
                    ? 'CSV or Excel files only (max 2 files, 16MB each)'
                    : 'Images, documents, or data files'}
                </p>
              </div>
            )}
          </div>
        )}

      {/* Centered error modal when uploadError active */}
      {uploadError && selectedTool && (selectedTool === 'file_upload' || selectedTool === 'data_analysis') && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 20000 }}
          aria-live="assertive"
          onMouseDown={(e) => {
            // Clicking backdrop closes dialog
            if (e.target === e.currentTarget) {
              logger.log('[upload-error-modal] Backdrop clicked');
              setUploadError(null);
              setAwaitingFileSelection(false);
              filesBeingProcessedRef.current = false;
              manuallyClearedToolRef.current = true;
              dispatch(setSelectedTool(null));
              dispatch(clearUploadedFiles());
              dispatch(clearTargetLanguages());
              dispatch(hideGlobalBlocking());
            }
          }}
        >
          <div
            className="flex items-start gap-3 p-4 rounded-md border border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-xl max-w-md w-full mx-4"
            onMouseDown={(e) => { e.stopPropagation(); }}
          >
          <div className="flex-shrink-0" aria-hidden="true">
            {/* Simple warning icon using radial exclamation */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l9 19H3L12 2z" />
              <line x1="12" y1="10" x2="12" y2="14" />
              <circle cx="12" cy="18" r="1" />
            </svg>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-sm">Could not upload file at this time.</span>
            <span className="text-xs">{uploadError.file}: {uploadError.message}</span>
            <div className="flex gap-2 mt-1 flex-wrap">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadError(null);
                  setAwaitingFileSelection(false);
                  filesBeingProcessedRef.current = false;
                  if (fileInputRef.current) {
                    fileInputRef.current.value='';
                    setTimeout(()=>{
                      fileInputRef.current?.click();
                    },0);
                  }
                }}
              >Select File Again</button>
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-400 dark:hover:bg-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadError(null);
                  setAwaitingFileSelection(false);
                  filesBeingProcessedRef.current = false;
                  manuallyClearedToolRef.current = true;
                  dispatch(setSelectedTool(null));
                  dispatch(clearUploadedFiles());
                  dispatch(clearTargetLanguages());
                  dispatch(hideGlobalBlocking());
                }}
              >Close</button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
        <Label.Root htmlFor="chat-input" className="sr-only">Chat Input</Label.Root>
        <div className="relative w-full group flex flex-col">
        {selectedTool !== 'translation' && (
          <div className="relative">
            {/* Batch dictation mode: show waveform instead of textarea while recording/processing/booting */}
            {dictationMode === 'batch' && (isSpeechStarting || isSpeechListening || isSpeechTranscribing) ? (
              <div
                className={`w-full ${isTablet ? 'min-h-[64px]' : 'min-h-[56px]'} bg-[var(--color-secondary)] rounded-t-lg 
                border-l border-r border-t border-[var(--color-accent)] group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-t-[#5E95ED]`}
                style={{ display: 'flex', alignItems: 'center', padding: '12px 0', position: 'relative' }}
              >
                <DictationWaveform
                  audioLevel={speechAudioLevel}
                  frequencyData={speechFrequencyData}
                  isProcessing={isSpeechTranscribing}
                  isBooting={isSpeechStarting}
                />
              </div>
            ) : (
            <textarea
              ref={textareaRef}
              id="chat-input"
              className={`w-full ${isTablet ? 'min-h-[64px]' : 'min-h-[56px]'} max-h-${iconSize || 32} bg-[var(--color-secondary)] text-[var(--color-text)] focus:outline-none resize-none rounded-t-lg 
              border-l border-r border-t border-[var(--color-accent)] group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-t-[#5E95ED]`}
              aria-label="Chat message input"
              aria-describedby={selectedTool ? "selected-tool" : undefined}
              // placeholder styling in index.css
              // While dictating, render live transcript in muted/placeholder colour so it
              // looks grayed-out (like the old placeholder) until the user stops.
              style={{
                ...textareaStyles,
                transition: 'color 0.3s ease, border-color 0.2s ease-in-out',
                color: (isSpeechListening && speechTranscript)
                  ? '#646971'                       // same gray used for scrollbarColor / placeholder
                  : undefined,                      // inherit var(--color-text) from textareaStyles
                cursor: isSpeechListening ? 'default' : undefined, // hide text cursor while dictating
              }}
              // Block all keyboard input while dictation is active.
              // readOnly only prevents user edits — the live-preview value prop is a
              // controlled React value so React can still update it freely each onresult.
              readOnly={isSpeechListening || isSpeechTranscribing}
              // 'Listening…' only shown when transcript is empty (value handles the preview)
              placeholder={isSpeechListening ? 'Listening…' : isSpeechTranscribing ? 'Transcribing…' : getPlaceholder()}
              // During listening in ghost mode: render preDictationValue + live transcript so the
              // preview is always visible even when the textarea already has content.
              // In batch mode, textarea is hidden during listening (waveform shown instead).
              value={
                dictationMode === 'ghost' && isSpeechListening && speechTranscript
                  ? (preDictationValueRef.current
                      ? preDictationValueRef.current + ' ' + speechTranscript
                      : speechTranscript)
                  : value
              }
              // No-op during listening: keeps `value` state equal to preDictationValue
              // so the processing effect appends correctly without double-committing.
              onChange={e => { if (!isSpeechListening) setValue(e.target.value); }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
            />
            )}
          </div>
        )}
        
        {/* Unified tool & file bubble container - always visible */}
          <div 
            ref={bottomBubbleContainerRef}
            id="selected-tool"
            className={`border-l border-r border-b ${selectedTool === 'translation' ? 'border-t rounded-t-lg' : ''} border-[var(--color-accent)] bg-[var(--color-secondary)] rounded-b-lg px-3 py-2 group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-b-[#5E95ED] ${selectedTool === 'translation' ? 'group-focus-within:!border-t-[#5E95ED]' : ''}`}
            style={{ boxShadow: 'none', transition: 'border-color 0.2s ease-in-out', scrollbarWidth: 'thin', scrollbarColor: 'var(--color-accent) transparent' }}
            role="group"
            aria-label="Tools and uploaded files"
          >
            {/* Translation Mode Inline UI - appears at top when translation mode is active */}
            {selectedTool === 'translation' && !isTranslationCollapsed && (
              <TranslationMode
                uploadedFiles={uploadedFiles}
                awaitingFileSelection={awaitingFileSelection}
                chatPage={chatPage}
                iconSize={iconSize}
                translationFileInputRef={translationFileInputRef}
                bottomBubbleContainerRef={bottomBubbleContainerRef}
                textareaRef={textareaRef}
                previousHeightRef={previousHeightRef}
                filesBeingProcessedRef={filesBeingProcessedRef}
                onHeightChange={onHeightChange}
                setAwaitingFileSelection={setAwaitingFileSelection}
                setSelectionWarning={(msg) => setFileWarning(msg ? { message: msg, mode: 'general' } : null)}
                setTranslationWarning={(warning) => setFileWarning(warning ? { ...warning, mode: 'translation' } : null)}
                handleRemoveFile={handleRemoveFile}
                handleTranslateDocuments={handleTranslateDocuments}
                handleFileChange={handleFileChange}
                clearChatInputDragOverlay={() => setIsDraggingOver(false)}
                abortTranslationFileReads={() => {
                  abortAndClearReaders(translationActiveReadersRef);
                }}
              />
            )}
            
            {/* Only show tools in Ask Chat mode */}
            {!shouldHideTools && (
              <>
                {/* Mobile: Plus icon button and selected tool display */}
                {isMobile ? (
                  <div className="flex flex-nowrap justify-start gap-2 items-center mb-1">
                    {/* Plus icon button to open tools menu */}
                      <button
                      type="button"
                      onClick={() => setIsMobileToolsMenuOpen(!isMobileToolsMenuOpen)}
                      style={{ color: 'var(--color-text)' }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--color-accent)] transition-all duration-200 flex-shrink-0"
                      aria-label="Open tools menu"
                      disabled={isFileDragInProgress}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>

                    {/* Display selected tool if any */}
                    {selectedTool && (() => {
                      const selectedOption = chatOptions.find(opt => opt.apiLabel === selectedTool);
                      if (!selectedOption) return null;
                      const IconComponent = selectedOption.icon as React.ComponentType<any>;
                      return (
                        <div className="flex items-center gap-1">
                          <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 h-8 rounded-lg text-xs font-medium border border-[#5E95ED] bg-[var(--color-accent)] text-white"
                          >
                            <span className="flex items-center justify-center">
                              <IconComponent width={iconSize} />
                            </span>
                            <span className="whitespace-nowrap">{selectedOption.label}</span>
                            <span
                              className="flex items-center justify-center cursor-pointer hover:opacity-70"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveTool();
                              }}
                            >
                              <Cross2Icon width={12} height={12} />
                            </span>
                          </div>
                          {/* Collapse/Expand icon for translation tool when active */}
                          {selectedTool === 'translation' && translationHistory.length > 0 && (
                            <div
                              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsTranslationCollapsed(!isTranslationCollapsed);
                              }}
                              aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                            >
                              {isTranslationCollapsed ? (
                                <ChevronUpIcon width={iconSize} />
                              ) : (
                                <ChevronDownIcon width={iconSize} />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                  </div>
                ) : (
                  /* Desktop/Tablet: Primary tools inline + secondary tools under + button */
                  <div className="flex items-center gap-2 mb-1">
                    {/* Plus button — opens secondary tools dropdown — outside overflow container so dropdown isn't clipped */}
                    <div className="relative flex-shrink-0">
                      <button
                        ref={desktopPlusButtonRef}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          if (!isDesktopPlusMenuOpen && desktopPlusButtonRef.current) {
                            const rect = desktopPlusButtonRef.current.getBoundingClientRect();
                            const dropdownHeight = 140; // approx height of 3-item menu
                            const spaceBelow = window.innerHeight - rect.bottom;
                            if (spaceBelow < dropdownHeight + 8) {
                              setDesktopPlusMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
                            } else {
                              setDesktopPlusMenuPos({ top: rect.bottom + 6, left: rect.left });
                            }
                          }
                          setIsDesktopPlusMenuOpen(prev => !prev);
                        }}
                        data-tutorial="plus-menu-button"
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-200 ${isDesktopPlusMenuOpen ? 'bg-[#5E95ED] border-[#5E95ED] text-white' : 'border-[var(--color-accent)] text-[var(--color-text)] hover:bg-[#5E95ED] hover:border-[#5E95ED] hover:text-white'} ${(isFileDragInProgress || isSpeechListening) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label="More tools"
                        disabled={isFileDragInProgress || isSpeechListening}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      </button>
                      {/* Secondary tools dropdown — rendered via portal so it's never clipped */}
                      {isDesktopPlusMenuOpen && desktopPlusMenuPos && createPortal(
                        <>
                          <style>{`
                            @keyframes dropdownSlideDown {
                              from { opacity: 0; transform: translateY(-6px) scale(0.97); }
                              to   { opacity: 1; transform: translateY(0)   scale(1);    }
                            }
                            @keyframes dropdownSlideUp {
                              from { opacity: 0; transform: translateY(6px) scale(0.97); }
                              to   { opacity: 1; transform: translateY(0)   scale(1);    }
                            }
                          `}</style>
                          {/* Click-outside overlay — suppressed while tutorial highlights a plus-menu item */}
                          {!isTutorialOnPlusMenuStep && (
                            <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => { setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); }} />
                          )}
                          <div
                            className="fixed bg-[var(--color-secondary)] border border-[var(--color-accent)] rounded-xl shadow-2xl overflow-hidden min-w-[210px]"
                            style={{
                              ...(desktopPlusMenuPos.top !== undefined ? { top: desktopPlusMenuPos.top } : { bottom: desktopPlusMenuPos.bottom }),
                              left: desktopPlusMenuPos.left,
                              zIndex: 9999,
                              animation: desktopPlusMenuPos.top !== undefined ? 'dropdownSlideDown 0.18s ease-out forwards' : 'dropdownSlideUp 0.18s ease-out forwards',
                            }}
                          >
                            {/* File Upload */}
                            {isFileUploadSupported && (
                            <button
                              type="button"
                              data-tutorial="byod-button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsFileUploadActive(true); triggerAdditionalUpload(); }}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                            >
                              <FileUploadIcon size={18} className="flex-shrink-0" />
                              <span className="text-sm top-heavy">File Upload</span>
                            </button>
                            )}
                            {isFileUploadSupported && <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />}
                            {/* Web Search — separate category between File Upload and secondary tools */}
                            {chatOptions.filter(t => t.apiLabel === 'web_search').map((toolOption) => {
                              const IconComponent = toolOption.icon as React.ComponentType<any>;
                              return (
                                <button
                                  key={toolOption.apiLabel}
                                  type="button"
                                  data-tutorial="web-search-tool"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); handleOptionClick(toolOption.apiLabel, e as any); }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <IconComponent size={18} className="flex-shrink-0" />
                                  <span className="text-sm top-heavy">{toolOption.label}</span>
                                </button>
                              );
                            })}
                            <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />
                            {/* Secondary tool options — sourced from chatOptions to stay in sync with mobile menu */}
                            {chatOptions.filter(t => ['data_analysis', 'translation'].includes(t.apiLabel)).map((toolOption) => {
                              const IconComponent = toolOption.icon as React.ComponentType<any>;
                              return (
                                <button
                                  key={toolOption.apiLabel}
                                  type="button"
                                  data-tutorial={toolOption.apiLabel === 'data_analysis' ? 'data-analysis-tool' : 'document-translation-tool'}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); handleOptionClick(toolOption.apiLabel, e as any); }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <IconComponent size={18} className="flex-shrink-0" />
                                  <span className="text-sm top-heavy">{toolOption.label}</span>
                                </button>
                              );
                            })}
                            {/* AI Model: Only show in dropdown if ModelSelector is NOT visible at the top */}
                            {isModelSelectorVisible(selectedTool) && !showModelSelector && (
                              <>
                                <button
                                  ref={desktopAiModelButtonRef}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    if (desktopAiModelButtonRef.current) {
                                      const rect = desktopAiModelButtonRef.current.getBoundingClientRect();
                                      const estimatedPanelHeight = Math.min((getVisibleModelOptions(selectedTool).length * 52) + 12, window.innerHeight - 16);
                                      const clampedTop = Math.max(8, Math.min(rect.top, window.innerHeight - estimatedPanelHeight - 8));
                                      setDesktopAiModelSubPanelPos({ top: clampedTop, left: rect.right + 6 });
                                    }
                                    setIsDesktopAiModelSubPanelOpen(prev => !prev);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                                    <rect x="2" y="3" width="20" height="14" rx="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                  </svg>
                                  <span className="text-sm flex-1 text-left top-heavy">AI Model</span>
                                  <ChevronRightIcon width={16} height={16} style={{ opacity: 0.6 }} />
                                </button>
                              </>
                            )}
                          </div>
                          {/* AI Model sub-panel */}
                          {isDesktopAiModelSubPanelOpen && desktopAiModelSubPanelPos && (
                            <div
                              className="fixed bg-[var(--color-secondary)] border border-[var(--color-accent)] rounded-xl shadow-2xl overflow-hidden min-w-[180px]"
                              style={{
                                top: desktopAiModelSubPanelPos.top,
                                left: desktopAiModelSubPanelPos.left,
                                zIndex: 10000,
                                animation: 'dropdownSlideDown 0.18s ease-out forwards',
                                maxHeight: 'calc(100vh - 16px)',
                                overflowY: 'auto',
                              }}
                            >
                              {getVisibleModelOptions(selectedTool).map((model: ModelConfig) => {
                                const isSelected = model.id === chatModeState.selectedModelId;
                                return (
                                  <button
                                    key={model.id}
                                    type="button"
                                    onClick={() => {
                                      handleModelSelection(model.id);
                                      setIsDesktopAiModelSubPanelOpen(false);
                                      setIsDesktopPlusMenuOpen(false);
                                    }}
                                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-sm transition-colors"
                                    style={{ color: isSelected ? '#5E95ED' : 'var(--color-text)' }}
                                  >
                                    <span>{model.displayName}</span>
                                    {isSelected && (
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>,
                        document.body
                      )}
                    </div>

                    {/* File Upload pill — shown immediately when selected from '+', count appears after files uploaded */}
                    {(() => {
                      const isFileUploadPillVisible = isFileUploadActive && effectiveToolForSend !== 'image_generation' && selectedTool !== 'data_analysis' && selectedTool !== 'translation';
                      return (
                        <div
                          style={{
                            maxWidth: isFileUploadPillVisible ? '200px' : '0px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            transition: 'max-width 300ms ease-in-out',
                          }}
                        >
                          <button
                            type="button"
                            aria-label={uploadedFiles.length > 0 ? `File Upload (${uploadedFiles.length}) — click to remove` : 'File Upload — click to remove'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (uploadedFiles.length > 0) {
                                handleClearAllFiles();
                              }
                              setIsFileUploadActive(false);
                            }}
                            className="flex-shrink-0 border-none cursor-pointer flex items-center gap-2 h-8 p-2 rounded-lg bg-[var(--color-accent)] text-white text-sm font-medium leading-none whitespace-nowrap"
                          >
                            <FileUploadIcon width={16} height={16} style={{ flexShrink: 0 }} />
                            <span style={{ transform: 'translateY(0.1rem)' }}>{uploadedFiles.length > 0 ? `File Upload (${uploadedFiles.length})` : 'File Upload'}</span>
                            <Cross2Icon width={14} height={14} style={{ display: 'block', flexShrink: 0 }} />
                          </button>
                        </div>
                      );
                    })()}

                    {/* Divider only visible when a secondary tool chip is active */}
                    {(selectedTool === 'data_analysis' || selectedTool === 'translation' || selectedTool === 'web_search') && (
                      <div style={{ height: '28px', width: '1px', background: 'var(--color-text)', margin: '0 8px' }} />
                    )}

                    {/* Primary tools + active secondary chip in scrollable row */}
                    <div className="flex flex-nowrap gap-2 items-center overflow-x-auto scrollbar-hide flex-1" aria-label="Available tools">

                    {/* Secondary tool chip + divider — wrapped so they occupy exactly ONE gap slot when active, zero space when not */}
                    {(() => {
                      const isAnySecondaryActive = selectedTool === 'data_analysis' || selectedTool === 'translation' || selectedTool === 'web_search';
                      return (
                        <div style={{ maxWidth: isAnySecondaryActive ? '350px' : '0px', marginRight: isAnySecondaryActive ? '0px' : '-8px', overflow: 'hidden', display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0, transition: 'max-width 300ms ease-in-out, margin-right 300ms ease-in-out' }}>
                          {chatOptions.filter(t => ['web_search', 'data_analysis', 'translation'].includes(t.apiLabel)).map(toolOption => {
                            const IconComponent = toolOption.icon as React.ComponentType<any>;
                            const isActive = selectedTool === toolOption.apiLabel;
                            const getTutorialAttr = (apiLabel: string) => {
                              if (apiLabel === 'web_search') return 'web-search-tool';
                              if (apiLabel === 'data_analysis') return 'data-analysis-tool';
                              return 'document-translation-tool';
                            };
                            return (
                              <button
                                key={toolOption.apiLabel}
                                type="button"
                                aria-pressed={isActive}
                                aria-hidden={!isActive}
                                tabIndex={isActive ? 0 : -1}
                                aria-label={isActive ? `${toolOption.label} selected` : toolOption.label}
                                data-tutorial={getTutorialAttr(toolOption.apiLabel)}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (isActive && !isSpeechListening) { handleRemoveTool(); } }}
                                className={`tool-button group relative inline-flex items-center gap-0 py-1 h-8 rounded-lg text-xs font-medium border overflow-hidden focus:outline-none flex-shrink-0 tool-button-active tool-button-desktop ${isSpeechListening ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                style={{ lineHeight: '1', maxWidth: isActive ? '200px' : '0px', paddingLeft: isActive ? '8px' : '0px', paddingRight: isActive ? '4px' : '0px', borderWidth: isActive ? '1px' : '0px', transition: 'max-width 300ms ease-in-out, padding-left 300ms ease-in-out, padding-right 300ms ease-in-out, border-width 300ms ease-in-out' }}
                              >
                                <span className="flex items-center justify-center" style={{ minWidth: '18px', height: '18px', lineHeight: '1' }}>
                                  <IconComponent width={18} height={18} size={18} style={{ display: 'block' }} />
                                </span>
                                <span className="whitespace-nowrap overflow-hidden top-heavy" style={{ paddingLeft: '6px', height: '18px', lineHeight: '18px', display: 'inline-block', position: 'relative', top: '0.075rem' }}>
                                  {toolOption.label}
                                </span>
                                <span
                                  className="flex items-center justify-center cursor-pointer"
                                  style={{ height: '18px', lineHeight: '1', paddingLeft: '12px', paddingRight: '4px', paddingTop: '0.05rem' }}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveTool(); }}
                                >
                                  <Cross2Icon width={14} height={14} style={{ display: 'block', flexShrink: 0 }} />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}



                    {selectedTool === 'translation' && translationHistory.length > 0 && (
                      <div
                        className="ml-auto cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsTranslationCollapsed(!isTranslationCollapsed); }}
                        aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                      >
                        {isTranslationCollapsed ? <ChevronUpIcon width={iconSize} /> : <ChevronDownIcon width={iconSize} />}
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Data Analysis upload spinner - show when uploading files for Data Analysis tool */}
            {isDataAnalysisUploading && (
              <div className="flex items-center gap-2 mb-2" style={{ color: '#5E95ED' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    border: '4px solid rgba(94, 149, 237, 0.25)',
                    borderTop: '4px solid #5E95ED',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                  aria-label="Uploading file..."
                />
                <span className="text-sm font-medium">Uploading file... Please wait</span>
              </div>
            )}

            {/* Collapsible file preview section - only when files are uploaded AND NOT in translation mode */}
            {uploadedFiles.length > 0 && selectedTool !== 'translation' && (
              <div className="flex flex-col gap-2">
                {/* Collapse/Expand header - only show on chatPage */}
                {chatPage && (
                  <button
                    type="button"
                    onClick={() => setIsFilePreviewCollapsed(!isFilePreviewCollapsed)}
                    className="flex items-center gap-1 text-xs text-[var(--color-text)] opacity-70 hover:opacity-100 transition-opacity self-start"
                    aria-label={isFilePreviewCollapsed ? 'Expand file previews' : 'Collapse file previews'}
                    aria-expanded={!isFilePreviewCollapsed}
                  >
                    {isFilePreviewCollapsed ? (
                      <>
                        <ChevronDownIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} attached</span>
                      </>
                    ) : (
                      <>
                        <ChevronUpIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">Hide files</span>
                      </>
                    )}
                  </button>
                )}
                {/* File preview content */}
                {!isFilePreviewCollapsed && (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Uploaded file representations: image thumbnails or generic file bubbles */}
                    {uploadedFiles.map(f => {
                      const isImage = /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name);
                if (isImage) {
                  return (
                    <PreviewableFileAttachment
                      key={f.key || f.name}
                      fileName={f.name}
                      fileType={f.type}
                      fileData={f.data}
                      dataSource="uploaded"
                    >
                      {({ openPreview }) => {
                        const mime = f.type.includes('jpeg') ? 'image/jpeg' : 'image/png';
                        const dataUrl = `data:${mime};base64,${f.data}`;
                        return (
                          <div
                            className="relative w-12 max-w-16 h-12 rounded-md overflow-hidden border border-[#5E95ED] bg-[#202024] dark:bg-[#202024] light:bg-[#E8F3FF] flex items-center justify-center shadow-sm cursor-pointer group"
                            style={{
                              backgroundColor: mode === 'dark' ? '#202024' : '#E8F3FF'
                            }}
                            aria-label={`Image file ${f.name}`}
                            onClick={openPreview}
                          >
                            <img
                              src={dataUrl}
                              alt={f.name}
                              className="object-cover w-full h-full group-hover:opacity-90"
                              style={{ imageRendering: 'auto' }}
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveFile(f.name); }}
                              aria-label={`Remove image ${f.name}`}
                              className="absolute top-0 right-0 m-0.5 bg-black/40 hover:bg-black/60 text-white rounded-full p-[2px] leading-none flex items-center justify-center"
                              style={{ lineHeight: 0 }}
                            >
                              <Cross2Icon width={12} height={12} />
                            </button>
                          </div>
                        );
                      }}
                    </PreviewableFileAttachment>
                  );
                }
                return (
                  <PreviewableFileAttachment
                    key={f.key || f.name}
                    fileName={f.name}
                    fileType={f.type}
                    fileData={f.data}
                    dataSource="uploaded"
                  >
                    {({ canPreview, openPreview }) => (
                      <div 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-[#5E95ED] text-[#5E95ED] shadow-sm"
                        style={{
                          backgroundColor: mode === 'dark' ? '#202024' : '#E8F3FF',
                          cursor: canPreview ? 'pointer' : 'default'
                        }}
                        aria-label={`File ${f.name}`}
                        onClick={canPreview ? openPreview : undefined}
                        ref={(el) => {
                          if (el) {
                            const span = el.querySelector('span.truncate');
                            if (span && span.scrollWidth > span.clientWidth) {
                              el.setAttribute('title', f.name);
                            } else {
                              el.removeAttribute('title');
                            }
                          }
                        }}
                      >
                        <span className="flex items-center justify-center flex-shrink-0" style={{ height: '14px' }}>
                          <FileIcon width={iconSize || 14} height={iconSize || 14} />
                        </span>
                        <span 
                          className="truncate leading-none" 
                          style={{ maxWidth: `${toolTextWidth}px`, paddingTop: '1px' }}
                        >
                          {f.name}
                        </span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveFile(f.name); }} className="p-0.5 rounded-full transition-colors flex-shrink-0 hover:bg-[#5E95ED] hover:text-white flex items-center justify-center" aria-label={`Remove uploaded file ${f.name}`}>
                          <Cross2Icon width={12} height={12} />
                        </button>
                      </div>
                    )}
                  </PreviewableFileAttachment>
                );
              })}
              {/* Action buttons for uploaded files */}
              {uploadedFiles.length >= 1 && (
                <button
                  type="button"
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded whitespace-nowrap min-w-fit leading-none transition-all"
                  style={{
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    backgroundColor: isSummarizeActive ? '#5E95ED' : 'transparent',
                    color: isSummarizeActive ? 'white' : '#5E95ED',
                    border: '2px solid #5E95ED',
                    opacity: isSummarizeActive ? 1 : 0.9,
                    fontWeight: isSummarizeActive ? '600' : '500',
                  }}
                  onClick={() => {
                    if (isSummarizeActive) {
                      setIsSummarizeActive(false);
                      setValue('');
                    } else {
                      setIsSummarizeActive(true);
                      setIsCompareActive(false);
                      if (isImageFileType(uploadedFiles[0].type)) {
                        setValue('Describe the image by providing a detailed analysis and highlighting the most interesting aspects without making assumptions. Give the result in Markdown format. Return the result in a summary and each component separately, delivering a structured output of its content.');
                      } else if (isDataFileType(uploadedFiles[0].type)) {
                        setValue('Analyze the data in the file.');
                      } else {
                        setValue('Summarize the content.');
                      }
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                  aria-label={isSummarizeActive ? "Cancel summarize" : "Summarize document"}
                >
                  {isSummarizeActive ? '✓ ' : ''}
                  {isImageFileType(uploadedFiles[0].type) ? 'Describe' : isDataFileType(uploadedFiles[0].type) ? 'Analyze' : 'Summarize'}
                </button>
              )}
              {/* Compare button - only show when exactly 2 text documents are uploaded */}
              {uploadedFiles.length === 2 && uploadedFiles.every(f => isTextDocumentType(f.type)) && (
                <button 
                  type="button" 
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded whitespace-nowrap min-w-fit leading-none transition-all" 
                  style={{ 
                    paddingTop: '5px', 
                    paddingBottom: '5px',
                    backgroundColor: isCompareActive ? '#5E95ED' : 'transparent',
                    color: isCompareActive ? 'white' : '#5E95ED',
                    border: isCompareActive ? '2px solid #5E95ED' : '2px solid #5E95ED',
                    opacity: isCompareActive ? 1 : 0.9,
                    fontWeight: isCompareActive ? '600' : '500'
                  }}

                  onClick={() => {
                    // Toggle Compare mode
                    if (isCompareActive) {
                      // Deactivate: clear prompt and reset state
                      setIsCompareActive(false);
                      setValue('');
                    } else {
                      // Activate: set prompt and mark as active
                      setIsCompareActive(true);
                      setIsSummarizeActive(false);
                      setValue('Compare these two files and highlight the key differences.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                  aria-label={isCompareActive ? "Cancel comparison" : "Compare documents"}
                >
                  {isCompareActive ? '✓ Compare' : 'Compare'}
                </button>
              )}
              
              {uploadedFiles.length > 1 && (
                <>
                  <button type="button" className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded bg-[var(--color-accent)] text-white hover:opacity-80 whitespace-nowrap leading-none" style={{ paddingTop: '5px', paddingBottom: '5px' }} onClick={handleClearAllFiles} aria-label="Clear all files">Clear</button>
                </>
              )}
                  </div>
                )}
              </div>
            )}
          </div>
          
        {/* Action buttons container - positioned on the right side with proper spacing */}
        {selectedTool !== 'translation' && (<div className="absolute right-4 flex items-center gap-0 pl-4" style={{ top: isMobile || isTablet ? '1px' : '8px' }}>

          {/* ── Batch Mode: Cancel (X) + Confirm (✓) buttons while listening ── */}
          {dictationMode === 'batch' && isSpeechListening && !isVoiceModeActive && !voiceModeLoading && (
            <>
              {/* Cancel dictation button (X) */}
              <button
                type="button"
                onClick={cancelDictation}
                aria-label="Cancel dictation"
                title="Cancel dictation"
                className="border-none"
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.2s ease',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <X size={20} />
              </button>
              {/* Confirm dictation button (✓) */}
              <button
                type="button"
                onClick={() => confirmDictation()}
                aria-label="Confirm dictation"
                title="Confirm dictation — send for transcription"
                className="border-none"
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#5E95ED',
                  transition: 'background-color 0.2s ease',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4a7fd9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#5E95ED'; }}
              >
                <Check size={20} />
              </button>
            </>
          )}

          {/* ── Batch Mode: Snowflake spinner while transcribing (no X button) ── */}
          {dictationMode === 'batch' && isSpeechTranscribing && !isSpeechListening && !isVoiceModeActive && !voiceModeLoading && (
            <>
              {/* Processing snowflake spinner only — no cancel button during Dictating... */}
              <div
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  className="dictation-snowflake-spinner"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ color: 'var(--color-text-muted, #9ca3af)' }}
                >
                  {/* 6 spokes */}
                  {[0, 30, 60, 90, 120, 150].map((angle) => (
                    <line
                      key={angle}
                      x1="12"
                      y1="2"
                      x2="12"
                      y2="22"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      transform={`rotate(${angle} 12 12)`}
                    />
                  ))}
                  {/* 12 tip dots */}
                  {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
                    <circle
                      key={`d-${angle}`}
                      cx="12"
                      cy="2.5"
                      r="1.2"
                      fill="currentColor"
                      transform={`rotate(${angle} 12 12)`}
                    />
                  ))}
                </svg>
              </div>
            </>
          )}

          {/* ── Dictation button (Mic) — shown when NOT actively recording/processing in batch mode ── */}
          {/* In ghost mode OR when idle in batch mode: show the standard mic button */}
          {!isVoiceModeActive && !voiceModeLoading && !(dictationMode === 'batch' && (isSpeechStarting || isSpeechListening || isSpeechTranscribing)) && (
            <button
              type="button"
              onClick={() => isSpeechListening ? stopSpeech() : handleStartSpeech()}
              disabled={isStreaming || isSpeechStarting || (!isSpeechListening && isSpeechTranscribing)}
              aria-label={isSpeechStarting ? 'Starting dictation…' : isSpeechListening ? 'Stop dictation' : isSpeechTranscribing ? 'Transcribing…' : 'Dictate (Ctrl+Shift+D)'}
              title={isSpeechStarting ? 'Starting dictation…' : isSpeechListening ? 'Listening… click to stop' : isSpeechTranscribing ? 'Transcribing…' : 'Dictate (Ctrl+Shift+D)'}
              className={`border-none disabled:opacity-50 disabled:cursor-not-allowed${isSpeechListening ? ' dictation-pulse-ring' : ''}`}
              onMouseEnter={(e) => {
                if (!isStreaming && !isSpeechTranscribing && !isSpeechStarting) {
                  e.currentTarget.style.backgroundColor = isSpeechListening ? '#B0030E' : 'var(--color-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSpeechListening ? '#D10411' : 'transparent';
              }}
              style={{
                display: 'flex',
                width: '40px',
                height: '40px',
                padding: '10px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: isSpeechListening ? '#D10411' : 'transparent',
                transition: 'background-color 0.2s ease',
                color: isSpeechListening ? '#FFFFFF' : 'var(--color-text)',
                cursor: (isStreaming || isSpeechStarting || (!isSpeechListening && isSpeechTranscribing)) ? 'not-allowed' : 'pointer',
                flexShrink: 0,
              }}
            >
              {isSpeechListening
                ? <MicOff size={20} color="#FFFFFF" />
                : (isSpeechTranscribing || isSpeechStarting)
                  ? <span className="animate-spin" style={{ width: 20, height: 20, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block' }} />
                  : <Mic size={20} />}
            </button>
          )}

          {/* Voice Mode button — visible when idle (no text, not streaming) OR when loading/active */}
          {/* Hidden during batch dictation recording/processing */}
          {!shouldHideTools && !(dictationMode === 'batch' && (isSpeechListening || isSpeechTranscribing)) && (voiceModeLoading || isVoiceModeActive || (!value.trim() && !isStreaming)) && (
            <button
              type="button"
              onClick={handleVoiceModeButtonClick}
              className="border-none transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={(isStreaming && !voiceModeLoading && !isVoiceModeActive) || isSpeechListening}
              aria-label={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : "Start voice mode"}
              title={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : isStreaming ? "Voice mode unavailable while responding" : "Start voice mode"}
              data-tutorial="voice-mode-button"
              onMouseEnter={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive && !isStreaming) {
                  e.currentTarget.style.backgroundColor = 'var(--color-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              style={{
                width: (voiceModeLoading || isVoiceModeActive) ? 'auto' : 'auto',
                height: 'auto',
                minWidth: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 'auto' : '120px') : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 0 : '8px') : 0,
                lineHeight: 0,
                padding: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? '8px' : '8px 16px') : '8px',
                backgroundColor: (voiceModeLoading || isVoiceModeActive) ? 'var(--color-accent)' : 'transparent',
                color: (voiceModeLoading || isVoiceModeActive) ? 'white' : 'var(--color-text)',
                transition: 'background-color 0.2s ease',
                marginLeft: '2px',
                marginRight: '2px',
                borderRadius: '8px',
                cursor: (isStreaming && !voiceModeLoading && !isVoiceModeActive) ? 'not-allowed' : 'pointer',
              }}
            >
              {voiceModeLoading ? (
                <>
                  <div
                    className="animate-spin rounded-full border-2 border-white border-t-transparent"
                    style={{ width: 16, height: 16}}
                  />
                  {!isMobile && (
                    <span className="text-sm font-medium" style={{ paddingTop: '2px', paddingLeft: '2px' }}>
                      Cancel
                    </span>
                  )}
                </>
              ) : isVoiceModeActive ? (
                <>
                  <AudioWaveform 
                    isActive={isVoiceModeActive} 
                    audioLevel={audioLevel}
                    frequencyData={frequencyData}
                    size={16}
                    color="white"
                  />
                  <span className="text-sm font-medium">End</span>
                </>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AudioLines size={iconSize || 24} />
                </span>
              )}
            </button>
          )}


          {/* Hidden camera input — mobile opens camera app directly (capture=environment) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', opacity: 0.01 }}
            onClick={() => {
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Hidden image input — mobile opens gallery/photo picker */}
          <input
            ref={mobileImageInputRef}
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            multiple
            style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', opacity: 0.01 }}
            onClick={() => {
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Hidden file input for actually uploading the file */}
          <input
            data-testid="chat-file-input"
            ref={fileInputRef}
            type="file"
            multiple
            // Dynamic accept: restricts to same file type as already uploaded files
            accept={getAllowedFileTypes}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '1px',
              height: '1px',
              opacity: 0.01,
            }}
            onClick={() => {
              filesBeingProcessedRef.current = false;
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Send/Stop button — replaces voice agent icon when text is present or streaming */}
          {/* In 3M Knowledge mode (shouldHideTools), send button is always visible (voice mode hidden) */}
          {/* Hidden during batch dictation listening/transcribing — confirm (✓) replaces it */}
          {(!!value.trim() || isStreaming || shouldHideTools) && !(dictationMode === 'batch' && (isSpeechListening || isSpeechTranscribing || isSpeechStarting)) && (
          <button
            type="button"
            onClick={() => {
              logger.log('[ChatInput] Stop/Send button clicked', { isStreaming, hasOnStopStream: !!onStopStream });
              if (isStreaming) {
                logger.log('[ChatInput] Calling onStopStream');
                onStopStream?.();
              } else {
                handleSendClick();
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => {
              if (value.trim() && !isSpeechListening) {
                e.currentTarget.style.backgroundColor = '#4a7fd9';
              }
            }}
            onMouseLeave={(e) => {
              if (value.trim() && !isSpeechListening) {
                e.currentTarget.style.backgroundColor = '#5E95ED';
              }
            }}
            className="border-none transition text-[var(--color-text)] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSpeechListening && !isStreaming}
            aria-label={isStreaming ? "Stop" : "Send"}
            style={{
              width: 'auto',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '4px',
              marginRight: '4px',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: (value.trim() && !isSpeechListening) ? '#5E95ED' : 'transparent',
              transition: 'background-color 0.2s ease',
            }}
          >
            {(isStreaming && !isImageStreaming) ? (
              <span style={{ width: iconSize || 24, height:  iconSize || 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StopIcon size={iconSize || 24} color="#D10411" />
              </span>
            ) : isStreaming ? ( <></> ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaperPlaneIcon
                  className="transition-colors"
                  width={iconSize || 20}
                  height={iconSize || 20}
                />
              </span>
            )}
          </button>
          )}
        </div>)}

          {/* Unified file warning dialog — handles both general/analysis and translation mode warnings */}
          {fileWarning && (fileWarning.mode === 'translation' || !isFileUploadDialogOpen) && (
            <FileSelectionWarningDialog
              title={fileWarning.title}
              subtitle={fileWarning.mode === 'translation'
                ? (fileWarning.subtitle !== undefined ? fileWarning.subtitle : 'The selected file could not be added to your translation.')
                : undefined}
              message={fileWarning.message}
              zIndex={19000}
              onClose={() => {
                setFileWarning(null);
                if (fileWarning.mode === 'general' && selectedTool === 'data_analysis' && uploadedFiles.length === 0) {
                  setIsFileUploadDialogOpen(true);
                }
              }}
            />
          )}

      {/* Custom blocking message for drag-drop attempts during spinner/streaming */}
      {showBlockingMessage && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          style={{ zIndex: 19500 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowBlockingMessage(false);
          }}
        >
          <div
            className="flex flex-col gap-3 p-4 rounded-md border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-sm w-full mx-4"
            onMouseDown={(e) => e.stopPropagation()}
            role="alert"
            aria-live="assertive"
            aria-label="Action blocked message"
          >
            <div className="flex items-start gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="1" />
              </svg>
              <span className="text-sm font-medium">Action not allowed in current state. Please try uploading later.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-[var(--color-accent)] text-white hover:opacity-80"
                onClick={() => setShowBlockingMessage(false)}
              >OK</button>
            </div>
          </div>
        </div>,
        document.body
      )}

        {/* File Upload Dialog */}
        <FileUploadDialog
          isOpen={isFileUploadDialogOpen}
          onClose={handleFileUploadDialogClose}
          onUpload={handleFileUploadDialogUpload}
          onDropzoneClick={handleFileUploadDialogDropzoneClick}
          uploadedFiles={stagedFiles}
          isUploading={stagedFiles.some(f => f.status === 'uploading')}
          isDataAnalysisMode={selectedTool === 'data_analysis'}
          onRemoveFile={handleRemoveStagedFile}
          onCancelFileRead={handleFileUploadDialogCancelFileRead}
          awaitingFileSelection={awaitingFileSelection}
          fileWarning={isFileUploadDialogOpen && fileWarning?.mode === 'general' ? fileWarning.message : null}
          fileWarningTitle={isFileUploadDialogOpen && fileWarning?.mode === 'general' ? (fileWarning.title ?? null) : null}
          onClearFileWarning={() => setFileWarning(null)}
          onFilesDropped={handleFilesDroppedOnDialog}
          hideDropzone={hideFileUploadDialogDropzone}
        />

        {/* Microphone Selector Dialog - MOVED outside motion.form; see below */}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {confirmationConfig && (
        <ConfirmationDialog
          isOpen={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={confirmationConfig.onConfirm}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          confirmText={confirmationConfig.confirmText}
        />
      )}

      {/* Mobile Tools Menu Modal */}
      {isMobile && isMobileToolsMenuOpen && !isMobileAiModelPanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setIsMobileToolsMenuOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Menu drawer from bottom */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">Source</h3>
              <button
                type="button"
                onClick={() => setIsMobileToolsMenuOpen(false)}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100"
                aria-label="Close menu"
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </div>
            <div className="border-t mx-5" style={{ borderColor: 'var(--color-border)' }} />

            {/* Tool toggles list */}
            <div className="px-2 py-2 max-h-[50vh] overflow-y-auto">
              {/* File Upload row */}
              {!shouldHideTools && isFileUploadSupported && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileToolsMenuOpen(false);
                      triggerAdditionalUpload();
                    }}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 active:bg-[var(--color-primary)]"
                  >
                    <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                      <FileUploadIconSVG width={22} height={22} style={{ color: 'var(--color-text)' }} />
                    </span>
                    <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">File Upload</span>
                  </button>
                </>
              )}

              {chatOptions.filter(toolOption => toolOption.apiLabel !== 'reasoning' && toolOption.apiLabel !== 'image_generation').map((toolOption) => {
                const IconComponent = toolOption.icon as React.ComponentType<any>;
                const isActive = selectedTool === toolOption.apiLabel;
                const isImageGenDisabled = isImageStreaming && !error && toolOption.apiLabel === 'image_generation';
                const isDisabled = isImageGenDisabled;

                return (
                  <React.Fragment key={toolOption.apiLabel}>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      if (isActive) {
                        handleRemoveTool();
                        setIsMobileToolsMenuOpen(false);
                      } else {
                        handleOptionClick(toolOption.apiLabel);
                      }
                    }}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 ${
                      isDisabled ? 'opacity-40 cursor-not-allowed' : 'active:bg-[var(--color-primary)]'
                    }`}
                  >
                    {/* Icon */}
                    <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                      <IconComponent width={22} height={22} size={22} style={{ display: 'block' }} />
                    </span>

                    {/* Label */}
                    <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">
                      {toolOption.label}
                      {isDisabled && toolOption.apiLabel === 'image_generation' && (
                        <span className="ml-1 text-xs opacity-60">(Generating…)</span>
                      )}
                    </span>

                    {/* Toggle switch */}
                    <span
                      className="relative inline-flex flex-shrink-0 items-center"
                      style={{ width: 38, height: 18 }}
                      aria-checked={isActive}
                      role="switch"
                    >
                      <span
                        style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 999,
                          backgroundColor: isActive ? '#2664D3' : 'var(--color-accent)',
                          opacity: isActive ? 1 : 0.35,
                          transition: 'background-color 0.2s ease',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: isActive ? 22 : 2,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          transition: 'left 0.2s ease',
                        }}
                      />
                    </span>
                  </button>
                  </React.Fragment>
                );
              })}

              {/* AI Model row — only visible when model selector is applicable */}
              {isModelSelectorVisible(selectedTool) && (
                <button
                  type="button"
                  onClick={() => setIsMobileAiModelPanelOpen(true)}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 active:bg-[var(--color-primary)]"
                >
                  <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </span>
                  <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">AI Model</span>
                  <span className="relative inline-flex flex-shrink-0 items-center justify-center" style={{ width: 38, height: 18 }}>
                    <ChevronRightIcon width={24} height={24} style={{ color: 'var(--color-text)', opacity: 0.6 }} />
                  </span>
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* AI Model Sub-Panel */}
      {isMobile && isMobileAiModelPanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setIsMobileAiModelPanelOpen(false)}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">AI Model</h3>
              <button
                type="button"
                onClick={() => setIsMobileAiModelPanelOpen(false)}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100"
                aria-label="Close AI Model panel"
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </div>
            <div className="px-2 py-2 pb-8">
              {getVisibleModelOptions(selectedTool).map((model: ModelConfig) => {
                const isSelected = model.id === chatModeState.selectedModelId;
                return (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() => {
                      handleModelSelection(model.id);
                      setIsMobileAiModelPanelOpen(false);
                      setIsMobileToolsMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all duration-150 active:bg-[var(--color-primary)]"
                    style={{ color: isSelected ? '#5E95ED' : 'var(--color-text)' }}
                  >
                    <span>{model.displayName}</span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
      {/* Dictation Error Dialog */}
      <ConfirmationDialog
        isOpen={showDictationError}
        title="Unable to Dictate"
        message="We're sorry, we're unable to dictate at the moment. Please try again later."
        onConfirm={() => setShowDictationError(false)}
        onCancel={() => setShowDictationError(false)}
        confirmText="OK"
        showCancelButton={false}
      />

      {/* Voice Mode Error Dialog */}
      <ConfirmationDialog
        isOpen={showVoiceModeError}
        title="Error: Could not connect to Voice Mode"
        message={voiceModeErrorMessage}
        onConfirm={() => setShowVoiceModeError(false)}
        onCancel={() => setShowVoiceModeError(false)}
        confirmText="OK"
        showCancelButton={false}
      />

      {/* Microphone Permission Dialog - only shown when permission is denied */}
      <MicrophonePermissionDialog
        isOpen={showMicPermissionDialog}
        onClose={() => setShowMicPermissionDialog(false)}
      />

    </motion.form>
      )}

      {/* No Audio Detected Dialog - state lives in Redux so it survives navigation/remounts */}
      <ConfirmationDialog
        isOpen={showNoAudioDialog}
        title="No Audio Detected"
        message="We're not detecting any sound from your microphone. Would you like to switch to a different microphone?"
        onConfirm={handleNoAudioDialogConfirm}
        onCancel={handleNoAudioDialogDismiss}
        confirmText="Switch Microphone"
        cancelText="Continue Anyway"
      />

      {/* Microphone Selector Dialog - outside motion.form so it renders even when voice mode hides the form */}
      <MicrophoneSelectorDialog
        isOpen={showMicSelector}
        onClose={() => setShowMicSelector(false)}
        onMicrophoneSelected={handleMicrophoneSelected}
      />

      {/* Inactivity warning dialog - rendered at ChatInput level so it persists after voice mode ends */}
      <InactivityWarningDialog 
        isOpen={showInactivityWarning}
        countdown={inactivityCountdown}
        disconnectedByInactivity={disconnectedByInactivity}
        onDismiss={handleDismissInactivityWarning}
      />
    </>
  );
}

export {};